import { useEffect, useState, useRef, useCallback } from 'react';
import { useNavigate, Routes, Route, Link, useLocation } from 'react-router-dom';
import { useLanguage } from '@/contexts/LanguageContext';
import { useAuth } from '@/contexts/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import {
  LayoutDashboard, UtensilsCrossed, ShoppingCart, ChefHat, Truck,
  Image, MessageSquare, LogOut, Globe, Menu, X, Plus, Trash2, Eye, EyeOff, ChevronLeft, Users, Tag, Sparkles, FileText, ExternalLink, Power, Briefcase, Store, Download, ShieldAlert
} from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Separator } from '@/components/ui/separator';
import { Switch } from '@/components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Auth-aware axios instance that reads token from localStorage
const axiosAuth = axios.create();
axiosAuth.interceptors.request.use((config) => {
  const token = localStorage.getItem('belgo_admin_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

function Sidebar({ active }) {
  const { t, lang, toggleLang } = useLanguage();
  const { logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  const links = [
    { to: '/admin', icon: LayoutDashboard, label: t('admin.stats') },
    { to: '/admin/menu', icon: UtensilsCrossed, label: t('admin.menu') },
    { to: '/admin/categories', icon: Tag, label: lang === 'fr' ? 'Catégories' : 'Categories' },
    { to: '/admin/modifiers', icon: Sparkles, label: lang === 'fr' ? 'Condiments & Extras' : 'Condiments & Extras' },
    { to: '/admin/home-content', icon: FileText, label: lang === 'fr' ? 'Page d\'accueil' : 'Home Page' },
    { to: '/admin/gallery', icon: Image, label: lang === 'fr' ? 'Galerie' : 'Gallery' },
    { to: '/admin/ueat', icon: ExternalLink, label: 'UEat' },
    { to: '/admin/ordering', icon: Power, label: lang === 'fr' ? 'Commandes ON/OFF' : 'Orders ON/OFF' },
    { to: '/admin/jobs', icon: Briefcase, label: lang === 'fr' ? 'Emplois' : 'Jobs' },
    { to: '/admin/retailers', icon: Store, label: lang === 'fr' ? 'Points de vente' : 'Retailers' },
    { to: '/admin/theme', icon: Sparkles, label: lang === 'fr' ? 'Apparence' : 'Theme' },
    { to: '/admin/orders', icon: ShoppingCart, label: t('admin.orders') },
    { to: '/admin/catering', icon: ChefHat, label: t('admin.catering') },
    { to: '/admin/foodtruck', icon: Truck, label: t('admin.foodtruck') },
    { to: '/admin/messages', icon: MessageSquare, label: t('admin.messages') },
    { to: '/admin/users', icon: Users, label: lang === 'fr' ? 'Utilisateurs' : 'Users' },
    { to: '/admin/holiday', icon: LayoutDashboard, label: lang === 'fr' ? 'Menu Fêtes' : 'Holiday Menu' },
    { to: '/admin/notifications', icon: MessageSquare, label: lang === 'fr' ? 'Notifications' : 'Notifications' },
    { to: '/admin/export', icon: Download, label: lang === 'fr' ? 'Exporter le code' : 'Export code' },
    { to: '/admin/settings', icon: LayoutDashboard, label: lang === 'fr' ? 'Paramètres' : 'Settings' },
  ];

  const nav = (
    <div className="flex flex-col h-full">
      <div className="p-4 border-b border-[#e0e0e0]">
        <Link to="/" className="flex items-center gap-2">
          <ChefHat className="w-6 h-6 text-[#e53a3a]" />
          <span className="font-heading text-lg font-bold text-[#e53a3a]">Belgo Admin</span>
        </Link>
      </div>
      <nav className="flex-1 p-3 space-y-1">
        {links.map(l => (
          <Link
            key={l.to}
            to={l.to}
            onClick={() => setOpen(false)}
            className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${
              active === l.to ? 'text-white' : 'text-[#1a1a1a] hover:bg-[#e0e0e0]'
            }`}
            style={active === l.to ? { backgroundColor: '#e53a3a' } : {}}
            data-testid={`admin-nav-${l.to.split('/').pop() || 'dashboard'}`}
          >
            <l.icon className="w-4 h-4" /> {l.label}
          </Link>
        ))}
      </nav>
      <div className="p-3 border-t border-[#e0e0e0] space-y-2">
        <button onClick={toggleLang} className="flex items-center gap-2 w-full px-3 py-2 rounded-lg text-sm hover:bg-[#e0e0e0]">
          <Globe className="w-4 h-4" /> {lang === 'fr' ? 'English' : 'Français'}
        </button>
        <Link to="/" className="flex items-center gap-2 w-full px-3 py-2 rounded-lg text-sm hover:bg-[#e0e0e0]">
          <ChevronLeft className="w-4 h-4" /> {lang === 'fr' ? 'Voir le site' : 'View site'}
        </Link>
        <button onClick={() => { logout(); navigate('/'); }} className="flex items-center gap-2 w-full px-3 py-2 rounded-lg text-sm text-red-600 hover:bg-red-50" data-testid="admin-logout">
          <LogOut className="w-4 h-4" /> {t('admin.logout')}
        </button>
      </div>
    </div>
  );

  return (
    <>
      {/* Mobile toggle */}
      <button className="lg:hidden fixed top-4 left-4 z-50 p-2 bg-white rounded-lg shadow-md border" onClick={() => setOpen(!open)}>
        {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
      </button>
      {/* Mobile sidebar */}
      {open && <div className="lg:hidden fixed inset-0 z-40 bg-black/30" onClick={() => setOpen(false)} />}
      <div className={`fixed lg:static inset-y-0 left-0 z-40 w-64 bg-white border-r border-[#e0e0e0] transform transition-transform lg:transform-none ${open ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}`}>
        {nav}
      </div>
    </>
  );
}

// Stats Dashboard
function StatsPage() {
  const { lang } = useLanguage();
  const [stats, setStats] = useState(null);
  useEffect(() => {
    axiosAuth.get(`${API}/admin/stats`).then(r => setStats(r.data)).catch(() => {});
  }, []);

  if (!stats) return <div className="p-8">{lang === 'fr' ? 'Chargement...' : 'Loading...'}</div>;

  const cards = [
    { label: { fr: 'Commandes totales', en: 'Total Orders' }, value: stats.orders_total, color: '#e53a3a' },
    { label: { fr: 'Commandes en attente', en: 'Pending Orders' }, value: stats.orders_pending, color: '#f4d00d' },
    { label: { fr: 'Demandes traiteur', en: 'Catering Requests' }, value: stats.catering_requests, color: '#2E7D32' },
    { label: { fr: 'Items au menu', en: 'Menu Items' }, value: stats.menu_items, color: '#555555' },
    { label: { fr: 'Messages non lus', en: 'Unread Messages' }, value: stats.unread_messages, color: '#D32F2F' },
  ];

  return (
    <div data-testid="admin-stats-page">
      <h2 className="font-heading text-2xl font-bold text-[#1a1a1a] mb-6">{lang === 'fr' ? 'Tableau de bord' : 'Dashboard'}</h2>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {cards.map((c) => (
          <div key={c.label.en} className="bg-white rounded-xl border border-[#e0e0e0] p-6" data-testid={`stat-card-${c.label.en}`}>
            <p className="text-sm text-[#555555] mb-1">{c.label[lang]}</p>
            <p className="text-3xl font-bold" style={{ color: c.color }}>{c.value}</p>
          </div>
        ))}
      </div>
    </div>
  );
}

// Menu Management
function MenuManagement() {
  const { lang } = useLanguage();
  const [items, setItems] = useState([]);
  const [categories, setCategories] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [form, setForm] = useState({ name_fr: '', name_en: '', description_fr: '', description_en: '', price: '', category: 'poutines', image_url: '', available: true, featured: false, options: [] });
  const [uploading, setUploading] = useState(false);
  const [search, setSearch] = useState('');
  const [filterCat, setFilterCat] = useState('all');

  const fetchItems = useCallback(() => axiosAuth.get(`${API}/menu`).then(r => setItems(r.data)), []);
  const fetchCategories = useCallback(() => axios.get(`${API}/categories`).then(r => setCategories(r.data)).catch(() => {}), []);
  useEffect(() => { fetchItems(); fetchCategories(); }, [fetchItems, fetchCategories]);

  const resetForm = () => setForm({ name_fr: '', name_en: '', description_fr: '', description_en: '', price: '', category: categories[0]?.key || 'poutines', image_url: '', available: true, featured: false, options: [] });

  const openEdit = (item) => {
    setEditItem(item);
    setForm({ ...item, price: String(item.price) });
    setDialogOpen(true);
  };

  const openNew = () => {
    setEditItem(null);
    resetForm();
    setDialogOpen(true);
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const { data } = await axiosAuth.post(`${API}/upload`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      const fullUrl = `${process.env.REACT_APP_BACKEND_URL}${data.url}`;
      setForm(f => ({ ...f, image_url: fullUrl }));
      toast.success(lang === 'fr' ? 'Image uploadée!' : 'Image uploaded!');
    } catch (err) {
      toast.error(err?.response?.data?.detail || (lang === 'fr' ? 'Erreur upload' : 'Upload error'));
    } finally {
      setUploading(false);
    }
  };

  const handleSave = async () => {
    if (!form.name_fr?.trim() || !form.name_en?.trim()) {
      toast.error(lang === 'fr' ? 'Les noms FR et EN sont requis' : 'FR and EN names are required');
      return;
    }
    if (!form.category) {
      toast.error(lang === 'fr' ? 'Veuillez choisir une catégorie' : 'Please select a category');
      return;
    }
    const price = parseFloat(form.price);
    if (isNaN(price) || price < 0) {
      toast.error(lang === 'fr' ? 'Prix invalide' : 'Invalid price');
      return;
    }
    // Build a clean payload (strip out extra fields like id, _id, created_at)
    const payload = {
      name_fr: form.name_fr,
      name_en: form.name_en,
      description_fr: form.description_fr || '',
      description_en: form.description_en || '',
      price,
      category: form.category,
      image_url: form.image_url || '',
      available: form.available !== false,
      featured: !!form.featured,
      options: Array.isArray(form.options) ? form.options : [],
    };
    try {
      if (editItem) {
        await axiosAuth.put(`${API}/menu/${editItem.id}`, payload);
      } else {
        await axiosAuth.post(`${API}/menu`, payload);
      }
      toast.success(lang === 'fr' ? 'Enregistré!' : 'Saved!');
      setDialogOpen(false);
      fetchItems();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const msg = typeof detail === 'string' ? detail : (Array.isArray(detail) ? detail.map(d => `${d.loc?.join('.')}: ${d.msg}`).join(' | ') : err?.message || 'Error');
      toast.error(msg);
    }
  };

  const handleToggle = async (id) => {
    await axiosAuth.patch(`${API}/menu/${id}/toggle`);
    fetchItems();
  };

  const handleDelete = async (id) => {
    if (!window.confirm(lang === 'fr' ? 'Supprimer cet item?' : 'Delete this item?')) return;
    await axiosAuth.delete(`${API}/menu/${id}`);
    fetchItems();
  };

  return (
    <div data-testid="admin-menu-page">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <h2 className="font-heading text-2xl font-bold text-[#1a1a1a]">{lang === 'fr' ? 'Gestion du menu' : 'Menu Management'}</h2>
        <button onClick={openNew} className="btn-belgo flex items-center gap-2 px-4 py-2 rounded-lg text-white text-sm font-semibold" style={{ backgroundColor: '#e53a3a' }} data-testid="add-menu-item">
          <Plus className="w-4 h-4" /> {lang === 'fr' ? 'Ajouter' : 'Add'}
        </button>
      </div>

      {/* Search + filter */}
      <div className="mb-4 flex flex-wrap gap-3 items-center">
        <div className="relative flex-1 min-w-[200px]">
          <input
            type="text"
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder={lang === 'fr' ? 'Rechercher un item...' : 'Search items...'}
            className="w-full pl-3 pr-10 py-2 rounded-lg border border-[#e0e0e0] bg-white text-sm focus:outline-none focus:ring-2 focus:ring-[#e53a3a]"
            data-testid="admin-menu-search"
          />
          {search && (
            <button onClick={() => setSearch('')} className="absolute right-2 top-1/2 -translate-y-1/2 text-[#888] hover:text-[#e53a3a]" data-testid="admin-menu-search-clear">
              <Plus className="w-4 h-4 rotate-45" />
            </button>
          )}
        </div>
        <select value={filterCat} onChange={e => setFilterCat(e.target.value)} className="rounded-lg border border-[#e0e0e0] bg-white px-3 py-2 text-sm" data-testid="admin-menu-filter-category">
          <option value="all">{lang === 'fr' ? 'Toutes catégories' : 'All categories'}</option>
          {categories.map(c => (
            <option key={c.id} value={c.key}>{lang === 'fr' ? c.name_fr : c.name_en}</option>
          ))}
        </select>
      </div>

      <div className="space-y-3">
        {(() => {
          const q = search.trim().toLowerCase();
          const filtered = items.filter(it => {
            if (filterCat !== 'all' && it.category !== filterCat) return false;
            if (!q) return true;
            return (it.name_fr || '').toLowerCase().includes(q)
              || (it.name_en || '').toLowerCase().includes(q)
              || (it.description_fr || '').toLowerCase().includes(q)
              || (it.description_en || '').toLowerCase().includes(q);
          });
          if (filtered.length === 0) return <p className="text-center py-8 text-[#555555]">{lang === 'fr' ? 'Aucun résultat' : 'No results'}</p>;
          return filtered.map(item => (
          <div key={item.id} className="flex items-center gap-4 bg-white rounded-xl border border-[#e0e0e0] p-4" data-testid={`admin-menu-${item.id}`}>
            {item.image_url ? (
              <img src={item.image_url} alt="" className="w-16 h-16 rounded-lg object-cover shrink-0" />
            ) : (
              <div className="w-16 h-16 rounded-lg bg-[#f5f5f0] shrink-0 flex items-center justify-center text-[#aaa]">
                <Image className="w-6 h-6" />
              </div>
            )}
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <h4 className="font-semibold text-[#1a1a1a] truncate">{lang === 'fr' ? item.name_fr : item.name_en}</h4>
                <Badge variant="outline" className="text-xs">{item.category}</Badge>
                {item.featured && <Badge className="text-xs" style={{ backgroundColor: '#f4d00d', color: 'black' }}>Vedette</Badge>}
              </div>
              <p className="text-sm text-[#e53a3a] font-medium">{item.price.toFixed(2)} $</p>
            </div>
            <Switch checked={item.available} onCheckedChange={() => handleToggle(item.id)} data-testid={`toggle-${item.id}`} />
            <button onClick={() => openEdit(item)} className="p-2 hover:bg-[#f5f5f0] rounded-lg text-sm text-[#555555]" data-testid={`edit-${item.id}`}>
              {lang === 'fr' ? 'Modifier' : 'Edit'}
            </button>
            <button onClick={() => handleDelete(item.id)} className="p-2 hover:bg-red-50 rounded-lg text-red-600" data-testid={`delete-${item.id}`}>
              <Trash2 className="w-4 h-4" />
            </button>
          </div>
          ));
        })()}
      </div>

      {/* Dialog for Add/Edit */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading">{editItem ? (lang === 'fr' ? 'Modifier l\'item' : 'Edit Item') : (lang === 'fr' ? 'Nouvel item' : 'New Item')}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Nom (FR)</Label>
                <Input value={form.name_fr} onChange={e => setForm(f => ({ ...f, name_fr: e.target.value }))} className="mt-1" data-testid="menu-form-name-fr" />
              </div>
              <div>
                <Label>Name (EN)</Label>
                <Input value={form.name_en} onChange={e => setForm(f => ({ ...f, name_en: e.target.value }))} className="mt-1" data-testid="menu-form-name-en" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Description (FR)</Label>
                <Textarea value={form.description_fr} onChange={e => setForm(f => ({ ...f, description_fr: e.target.value }))} rows={2} className="mt-1" />
              </div>
              <div>
                <Label>Description (EN)</Label>
                <Textarea value={form.description_en} onChange={e => setForm(f => ({ ...f, description_en: e.target.value }))} rows={2} className="mt-1" />
              </div>
            </div>
            <div className="grid grid-cols-3 gap-4">
              <div>
                <Label>{lang === 'fr' ? 'Prix ($)' : 'Price ($)'}</Label>
                <Input type="number" step="0.01" value={form.price} onChange={e => setForm(f => ({ ...f, price: e.target.value }))} className="mt-1" data-testid="menu-form-price" />
              </div>
              <div>
                <Label>{lang === 'fr' ? 'Catégorie' : 'Category'}</Label>
                <select value={form.category} onChange={e => setForm(f => ({ ...f, category: e.target.value }))} className="mt-1 w-full rounded-md border border-[#e0e0e0] px-3 py-2 text-sm bg-white" data-testid="menu-form-category">
                  {categories.length === 0 ? (
                    <option value="">{lang === 'fr' ? 'Aucune catégorie' : 'No categories'}</option>
                  ) : categories.map(c => (
                    <option key={c.id} value={c.key}>{lang === 'fr' ? c.name_fr : c.name_en}</option>
                  ))}
                </select>
              </div>
              <div>
                <Label>{lang === 'fr' ? 'Image (upload ou URL)' : 'Image (upload or URL)'}</Label>
                <Input value={form.image_url} onChange={e => setForm(f => ({ ...f, image_url: e.target.value }))} className="mt-1" placeholder="https://..." data-testid="menu-form-image-url" />
                <input type="file" accept="image/*" onChange={handleImageUpload} disabled={uploading} className="mt-2 text-xs" data-testid="menu-form-image-file" />
                {uploading && <p className="text-xs text-[#555555] mt-1">{lang === 'fr' ? 'Téléchargement...' : 'Uploading...'}</p>}
                {form.image_url && <img src={form.image_url} alt="preview" className="mt-2 w-20 h-20 object-cover rounded" />}
              </div>
            </div>
            <div className="flex items-center gap-6">
              <label className="flex items-center gap-2 text-sm">
                <Switch checked={form.available} onCheckedChange={v => setForm(f => ({ ...f, available: v }))} />
                {lang === 'fr' ? 'Disponible' : 'Available'}
              </label>
              <label className="flex items-center gap-2 text-sm">
                <Switch checked={form.featured} onCheckedChange={v => setForm(f => ({ ...f, featured: v }))} />
                Vedette / Featured
              </label>
            </div>

            {/* Sizes / Format options */}
            <div className="border-t pt-4">
              <div className="flex items-center justify-between mb-2">
                <Label>{lang === 'fr' ? 'Formats / Grandeurs' : 'Sizes / Formats'}</Label>
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => setForm(f => ({ ...f, options: [
                      { name_fr: 'Petite', name_en: 'Small', price_modifier: -2 },
                      { name_fr: 'Régulière', name_en: 'Regular', price_modifier: 0 },
                      { name_fr: 'Grande', name_en: 'Large', price_modifier: 3 },
                    ] }))}
                    className="text-xs px-2 py-1 rounded border border-[#e0e0e0] hover:bg-[#f5f5f0]"
                    data-testid="menu-form-sizes-preset"
                  >
                    {lang === 'fr' ? 'Préréglage Petite/Rég/Grande' : 'Preset Small/Reg/Large'}
                  </button>
                  <button
                    type="button"
                    onClick={() => setForm(f => ({ ...f, options: [...(f.options || []), { name_fr: '', name_en: '', price_modifier: 0 }] }))}
                    className="text-xs px-2 py-1 rounded text-white"
                    style={{ backgroundColor: '#e53a3a' }}
                    data-testid="menu-form-add-size"
                  >
                    + {lang === 'fr' ? 'Ajouter' : 'Add'}
                  </button>
                </div>
              </div>
              <p className="text-xs text-[#888] mb-2">{lang === 'fr' ? 'Laissez vide si l\'item n\'a pas de format. Le modificateur s\'ajoute (ou se soustrait) au prix de base.' : 'Leave empty if item has no size. Modifier adds (or subtracts) from base price.'}</p>
              {(form.options || []).length === 0 ? (
                <p className="text-xs text-[#aaa] italic">{lang === 'fr' ? 'Aucun format défini' : 'No sizes defined'}</p>
              ) : (
                <div className="space-y-2">
                  {(form.options || []).map((opt, idx) => (
                    <div key={idx} className="grid grid-cols-12 gap-2 items-center" data-testid={`menu-form-size-${idx}`}>
                      <Input
                        value={opt.name_fr || ''}
                        onChange={e => setForm(f => ({ ...f, options: f.options.map((o, i) => i === idx ? { ...o, name_fr: e.target.value } : o) }))}
                        placeholder="Nom FR"
                        className="col-span-4"
                      />
                      <Input
                        value={opt.name_en || ''}
                        onChange={e => setForm(f => ({ ...f, options: f.options.map((o, i) => i === idx ? { ...o, name_en: e.target.value } : o) }))}
                        placeholder="Name EN"
                        className="col-span-4"
                      />
                      <Input
                        type="number"
                        step="0.01"
                        value={opt.price_modifier ?? 0}
                        onChange={e => setForm(f => ({ ...f, options: f.options.map((o, i) => i === idx ? { ...o, price_modifier: parseFloat(e.target.value) || 0 } : o) }))}
                        placeholder="±$"
                        className="col-span-3"
                      />
                      <button
                        type="button"
                        onClick={() => setForm(f => ({ ...f, options: f.options.filter((_, i) => i !== idx) }))}
                        className="col-span-1 p-2 text-red-600 hover:bg-red-50 rounded"
                        data-testid={`menu-form-remove-size-${idx}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <button onClick={handleSave} className="btn-belgo w-full px-4 py-2.5 rounded-lg text-white font-semibold" style={{ backgroundColor: '#e53a3a' }} data-testid="menu-form-save">
              {lang === 'fr' ? 'Enregistrer' : 'Save'}
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// Categories Management
function CategoriesManagement() {
  const { lang } = useLanguage();
  const [cats, setCats] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editCat, setEditCat] = useState(null);
  const [form, setForm] = useState({ key: '', name_fr: '', name_en: '', order: 0 });

  const fetchCats = useCallback(() => axios.get(`${API}/categories`).then(r => setCats(r.data)).catch(() => {}), []);
  useEffect(() => { fetchCats(); }, [fetchCats]);

  const openNew = () => {
    setEditCat(null);
    setForm({ key: '', name_fr: '', name_en: '', order: cats.length + 1 });
    setDialogOpen(true);
  };

  const openEdit = (cat) => {
    setEditCat(cat);
    setForm({ key: cat.key, name_fr: cat.name_fr, name_en: cat.name_en, order: cat.order || 0 });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!form.key.trim() || !form.name_fr.trim() || !form.name_en.trim()) {
      toast.error(lang === 'fr' ? 'Tous les champs sont requis' : 'All fields are required');
      return;
    }
    const payload = { ...form, order: parseInt(form.order) || 0 };
    try {
      if (editCat) {
        await axiosAuth.put(`${API}/categories/${editCat.id}`, payload);
      } else {
        await axiosAuth.post(`${API}/categories`, payload);
      }
      toast.success(lang === 'fr' ? 'Enregistré!' : 'Saved!');
      setDialogOpen(false);
      fetchCats();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Error');
    }
  };

  const handleDelete = async (cat) => {
    if (!window.confirm(lang === 'fr' ? `Supprimer la catégorie "${cat.name_fr}"?` : `Delete the category "${cat.name_en}"?`)) return;
    try {
      await axiosAuth.delete(`${API}/categories/${cat.id}`);
      toast.success(lang === 'fr' ? 'Supprimée' : 'Deleted');
      fetchCats();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Error');
    }
  };

  return (
    <div data-testid="admin-categories-page">
      <div className="flex items-center justify-between mb-6">
        <h2 className="font-heading text-2xl font-bold text-[#1a1a1a]">{lang === 'fr' ? 'Catégories du menu' : 'Menu Categories'}</h2>
        <button onClick={openNew} className="btn-belgo flex items-center gap-2 px-4 py-2 rounded-lg text-white text-sm font-semibold" style={{ backgroundColor: '#e53a3a' }} data-testid="add-category-btn">
          <Plus className="w-4 h-4" /> {lang === 'fr' ? 'Ajouter une catégorie' : 'Add Category'}
        </button>
      </div>

      <div className="bg-white rounded-xl border border-[#e0e0e0] overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-[#f5f5f0] text-[#555555]">
            <tr>
              <th className="text-left p-3">{lang === 'fr' ? 'Ordre' : 'Order'}</th>
              <th className="text-left p-3">{lang === 'fr' ? 'Clé' : 'Key'}</th>
              <th className="text-left p-3">{lang === 'fr' ? 'Nom (FR)' : 'Name (FR)'}</th>
              <th className="text-left p-3">{lang === 'fr' ? 'Nom (EN)' : 'Name (EN)'}</th>
              <th className="text-right p-3">{lang === 'fr' ? 'Actions' : 'Actions'}</th>
            </tr>
          </thead>
          <tbody>
            {cats.length === 0 && (
              <tr><td colSpan="5" className="p-6 text-center text-[#555555]">{lang === 'fr' ? 'Aucune catégorie' : 'No categories'}</td></tr>
            )}
            {cats.map(c => (
              <tr key={c.id} className="border-t border-[#e0e0e0]" data-testid={`category-row-${c.key}`}>
                <td className="p-3 font-semibold">{c.order}</td>
                <td className="p-3 font-mono text-xs text-[#555555]">{c.key}</td>
                <td className="p-3">{c.name_fr}</td>
                <td className="p-3">{c.name_en}</td>
                <td className="p-3 text-right">
                  <button onClick={() => openEdit(c)} className="px-3 py-1 text-xs rounded hover:bg-[#f5f5f0] text-[#555555]" data-testid={`edit-category-${c.key}`}>
                    {lang === 'fr' ? 'Modifier' : 'Edit'}
                  </button>
                  <button onClick={() => handleDelete(c)} className="px-3 py-1 text-xs rounded hover:bg-red-50 text-red-600 ml-1" data-testid={`delete-category-${c.key}`}>
                    <Trash2 className="w-3 h-3 inline" />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading">{editCat ? (lang === 'fr' ? 'Modifier la catégorie' : 'Edit Category') : (lang === 'fr' ? 'Nouvelle catégorie' : 'New Category')}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>{lang === 'fr' ? 'Clé (identifiant)' : 'Key (identifier)'}</Label>
              <Input value={form.key} onChange={e => setForm(f => ({ ...f, key: e.target.value }))} placeholder="ex: entrees" className="mt-1 font-mono" data-testid="category-form-key" />
              <p className="text-xs text-[#555555] mt-1">{lang === 'fr' ? 'Minuscules, sans espace (ex: entrees, plats_principaux)' : 'Lowercase, no spaces (e.g., entrees, main_dishes)'}</p>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Nom (FR)</Label>
                <Input value={form.name_fr} onChange={e => setForm(f => ({ ...f, name_fr: e.target.value }))} className="mt-1" data-testid="category-form-name-fr" />
              </div>
              <div>
                <Label>Name (EN)</Label>
                <Input value={form.name_en} onChange={e => setForm(f => ({ ...f, name_en: e.target.value }))} className="mt-1" data-testid="category-form-name-en" />
              </div>
            </div>
            <div>
              <Label>{lang === 'fr' ? 'Ordre d\'affichage' : 'Display order'}</Label>
              <Input type="number" value={form.order} onChange={e => setForm(f => ({ ...f, order: e.target.value }))} className="mt-1" data-testid="category-form-order" />
            </div>
            <button onClick={handleSave} className="btn-belgo w-full px-4 py-2.5 rounded-lg text-white font-semibold" style={{ backgroundColor: '#e53a3a' }} data-testid="category-form-save">
              {lang === 'fr' ? 'Enregistrer' : 'Save'}
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// Modifiers Management (Condiments & Extras)
function ModifiersManagement() {
  const { lang } = useLanguage();
  const [activeTab, setActiveTab] = useState('condiment');
  const [mods, setMods] = useState([]);
  const [categories, setCategories] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editMod, setEditMod] = useState(null);
  const [form, setForm] = useState({ type: 'condiment', name_fr: '', name_en: '', price: '0', categories: [], available: true, order: 0 });

  const fetchAll = useCallback(async () => {
    const [m, c] = await Promise.all([
      axios.get(`${API}/modifiers`).catch(() => ({ data: [] })),
      axios.get(`${API}/categories`).catch(() => ({ data: [] })),
    ]);
    setMods(m.data);
    setCategories(c.data);
  }, []);
  useEffect(() => { fetchAll(); }, [fetchAll]);

  const filtered = mods.filter(m => m.type === activeTab);

  const openNew = () => {
    setEditMod(null);
    setForm({ type: activeTab, name_fr: '', name_en: '', price: '0', categories: [], available: true, order: filtered.length + 1 });
    setDialogOpen(true);
  };

  const openEdit = (mod) => {
    setEditMod(mod);
    setForm({
      type: mod.type,
      name_fr: mod.name_fr,
      name_en: mod.name_en,
      price: String(mod.price ?? 0),
      categories: mod.categories || [],
      available: mod.available !== false,
      order: mod.order || 0,
    });
    setDialogOpen(true);
  };

  const toggleCategory = (key) => {
    setForm(f => ({
      ...f,
      categories: f.categories.includes(key) ? f.categories.filter(k => k !== key) : [...f.categories, key],
    }));
  };

  const handleSave = async () => {
    if (!form.name_fr.trim() || !form.name_en.trim()) {
      toast.error(lang === 'fr' ? 'Noms FR et EN requis' : 'FR and EN names required');
      return;
    }
    if (form.categories.length === 0) {
      toast.error(lang === 'fr' ? 'Sélectionnez au moins une catégorie' : 'Select at least one category');
      return;
    }
    const payload = {
      type: form.type,
      name_fr: form.name_fr.trim(),
      name_en: form.name_en.trim(),
      price: form.type === 'extra' ? (parseFloat(form.price) || 0) : 0,
      categories: form.categories,
      available: form.available,
      order: parseInt(form.order) || 0,
    };
    try {
      if (editMod) {
        await axiosAuth.put(`${API}/modifiers/${editMod.id}`, payload);
      } else {
        await axiosAuth.post(`${API}/modifiers`, payload);
      }
      toast.success(lang === 'fr' ? 'Enregistré!' : 'Saved!');
      setDialogOpen(false);
      fetchAll();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const msg = typeof detail === 'string' ? detail : (Array.isArray(detail) ? detail.map(d => `${d.loc?.join('.')}: ${d.msg}`).join(' | ') : err?.message || 'Error');
      toast.error(msg);
    }
  };

  const handleDelete = async (mod) => {
    if (!window.confirm(lang === 'fr' ? `Supprimer "${mod.name_fr}"?` : `Delete "${mod.name_en}"?`)) return;
    try {
      await axiosAuth.delete(`${API}/modifiers/${mod.id}`);
      toast.success(lang === 'fr' ? 'Supprimé' : 'Deleted');
      fetchAll();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Error');
    }
  };

  const catLabel = (key) => {
    const c = categories.find(x => x.key === key);
    if (!c) return key;
    return lang === 'fr' ? c.name_fr : c.name_en;
  };

  return (
    <div data-testid="admin-modifiers-page">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <h2 className="font-heading text-2xl font-bold text-[#1a1a1a]">{lang === 'fr' ? 'Condiments & Extras' : 'Condiments & Extras'}</h2>
        <button onClick={openNew} className="btn-belgo flex items-center gap-2 px-4 py-2 rounded-lg text-white text-sm font-semibold" style={{ backgroundColor: '#e53a3a' }} data-testid="add-modifier-btn">
          <Plus className="w-4 h-4" /> {lang === 'fr' ? `Ajouter ${activeTab === 'condiment' ? 'un condiment' : 'un extra'}` : `Add ${activeTab}`}
        </button>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 mb-6 border-b border-[#e0e0e0]">
        {['condiment', 'extra'].map(t => (
          <button
            key={t}
            onClick={() => setActiveTab(t)}
            className={`px-5 py-2.5 text-sm font-medium border-b-2 transition-colors ${activeTab === t ? 'border-[#e53a3a] text-[#e53a3a]' : 'border-transparent text-[#555555] hover:text-[#1a1a1a]'}`}
            data-testid={`modifier-tab-${t}`}
          >
            {t === 'condiment' ? (lang === 'fr' ? 'Condiments (gratuits)' : 'Condiments (free)') : (lang === 'fr' ? 'Extras (payants)' : 'Extras (paid)')}
            <span className="ml-2 text-xs text-[#888]">({mods.filter(m => m.type === t).length})</span>
          </button>
        ))}
      </div>

      {/* List */}
      <div className="bg-white rounded-xl border border-[#e0e0e0] overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-[#f5f5f0] text-[#555555]">
            <tr>
              <th className="text-left p-3">{lang === 'fr' ? 'Nom' : 'Name'}</th>
              {activeTab === 'extra' && <th className="text-left p-3">{lang === 'fr' ? 'Prix' : 'Price'}</th>}
              <th className="text-left p-3">{lang === 'fr' ? 'Catégories' : 'Categories'}</th>
              <th className="text-left p-3">{lang === 'fr' ? 'Dispo' : 'Avail.'}</th>
              <th className="text-right p-3">Actions</th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 ? (
              <tr><td colSpan={activeTab === 'extra' ? 5 : 4} className="p-6 text-center text-[#555555]">{lang === 'fr' ? 'Aucun élément' : 'No items'}</td></tr>
            ) : filtered.map(m => (
              <tr key={m.id} className="border-t border-[#e0e0e0]" data-testid={`modifier-row-${m.id}`}>
                <td className="p-3">
                  <div className="font-medium">{lang === 'fr' ? m.name_fr : m.name_en}</div>
                  <div className="text-xs text-[#888]">{lang === 'fr' ? m.name_en : m.name_fr}</div>
                </td>
                {activeTab === 'extra' && <td className="p-3 font-semibold text-[#e53a3a]">+{(m.price || 0).toFixed(2)} $</td>}
                <td className="p-3">
                  <div className="flex flex-wrap gap-1">
                    {(m.categories || []).map(k => (
                      <Badge key={k} variant="outline" className="text-xs">{catLabel(k)}</Badge>
                    ))}
                  </div>
                </td>
                <td className="p-3">{m.available ? '✓' : '✗'}</td>
                <td className="p-3 text-right whitespace-nowrap">
                  <button onClick={() => openEdit(m)} className="px-3 py-1 text-xs rounded hover:bg-[#f5f5f0] text-[#555555]" data-testid={`edit-modifier-${m.id}`}>
                    {lang === 'fr' ? 'Modifier' : 'Edit'}
                  </button>
                  <button onClick={() => handleDelete(m)} className="px-3 py-1 text-xs rounded hover:bg-red-50 text-red-600 ml-1" data-testid={`delete-modifier-${m.id}`}>
                    <Trash2 className="w-3 h-3 inline" />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading">
              {editMod ? (lang === 'fr' ? 'Modifier' : 'Edit') : (lang === 'fr' ? 'Nouveau' : 'New')} — {form.type === 'condiment' ? (lang === 'fr' ? 'Condiment (gratuit)' : 'Condiment (free)') : (lang === 'fr' ? 'Extra (payant)' : 'Extra (paid)')}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            {!editMod && (
              <div>
                <Label>Type</Label>
                <select value={form.type} onChange={e => setForm(f => ({ ...f, type: e.target.value }))} className="mt-1 w-full rounded-md border border-[#e0e0e0] px-3 py-2 text-sm bg-white" data-testid="modifier-form-type">
                  <option value="condiment">{lang === 'fr' ? 'Condiment (gratuit)' : 'Condiment (free)'}</option>
                  <option value="extra">{lang === 'fr' ? 'Extra (payant)' : 'Extra (paid)'}</option>
                </select>
              </div>
            )}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Nom (FR)</Label>
                <Input value={form.name_fr} onChange={e => setForm(f => ({ ...f, name_fr: e.target.value }))} className="mt-1" data-testid="modifier-form-name-fr" />
              </div>
              <div>
                <Label>Name (EN)</Label>
                <Input value={form.name_en} onChange={e => setForm(f => ({ ...f, name_en: e.target.value }))} className="mt-1" data-testid="modifier-form-name-en" />
              </div>
            </div>
            {form.type === 'extra' && (
              <div>
                <Label>{lang === 'fr' ? 'Prix supplémentaire ($)' : 'Extra price ($)'}</Label>
                <Input type="number" step="0.01" min="0" value={form.price} onChange={e => setForm(f => ({ ...f, price: e.target.value }))} className="mt-1" data-testid="modifier-form-price" />
              </div>
            )}
            <div>
              <Label>{lang === 'fr' ? 'S\'applique aux catégories' : 'Applies to categories'}</Label>
              <div className="mt-2 grid grid-cols-2 gap-2 p-3 bg-[#f5f5f0] rounded-md max-h-48 overflow-y-auto">
                {categories.map(c => (
                  <label key={c.key} className="flex items-center gap-2 text-sm cursor-pointer hover:bg-white p-1.5 rounded">
                    <input
                      type="checkbox"
                      checked={form.categories.includes(c.key)}
                      onChange={() => toggleCategory(c.key)}
                      className="accent-[#e53a3a]"
                      data-testid={`modifier-form-cat-${c.key}`}
                    />
                    <span>{lang === 'fr' ? c.name_fr : c.name_en}</span>
                  </label>
                ))}
              </div>
              <p className="text-xs text-[#888] mt-1">{lang === 'fr' ? `${form.categories.length} catégorie(s) sélectionnée(s)` : `${form.categories.length} category(ies) selected`}</p>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>{lang === 'fr' ? 'Ordre' : 'Order'}</Label>
                <Input type="number" value={form.order} onChange={e => setForm(f => ({ ...f, order: e.target.value }))} className="mt-1" data-testid="modifier-form-order" />
              </div>
              <div className="flex items-end">
                <label className="flex items-center gap-2 text-sm pb-2">
                  <Switch checked={form.available} onCheckedChange={v => setForm(f => ({ ...f, available: v }))} />
                  {lang === 'fr' ? 'Disponible' : 'Available'}
                </label>
              </div>
            </div>
            <button onClick={handleSave} className="btn-belgo w-full px-4 py-2.5 rounded-lg text-white font-semibold" style={{ backgroundColor: '#e53a3a' }} data-testid="modifier-form-save">
              {lang === 'fr' ? 'Enregistrer' : 'Save'}
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// Reusable image upload field
function ImageUploadField({ value, onChange, label, testId }) {
  const { lang } = useLanguage();
  const [uploading, setUploading] = useState(false);
  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const { data } = await axiosAuth.post(`${API}/upload`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      const fullUrl = `${process.env.REACT_APP_BACKEND_URL}${data.url}`;
      onChange(fullUrl);
      toast.success(lang === 'fr' ? 'Image uploadée!' : 'Image uploaded!');
    } catch (err) {
      toast.error(err?.response?.data?.detail || (lang === 'fr' ? 'Erreur upload' : 'Upload error'));
    } finally {
      setUploading(false);
    }
  };
  return (
    <div>
      <Label>{label}</Label>
      <Input value={value || ''} onChange={e => onChange(e.target.value)} placeholder="https://..." className="mt-1" data-testid={`${testId}-url`} />
      <input type="file" accept="image/*" onChange={handleUpload} disabled={uploading} className="mt-2 text-xs" data-testid={`${testId}-file`} />
      {uploading && <p className="text-xs text-[#555555] mt-1">{lang === 'fr' ? 'Téléchargement...' : 'Uploading...'}</p>}
      {value && <img src={value} alt="preview" className="mt-2 w-32 h-20 object-cover rounded border" />}
    </div>
  );
}

// Home Content Editor (CMS)
function HomeContentEditor() {
  const { lang } = useLanguage();
  const [content, setContent] = useState(null);
  const [saving, setSaving] = useState(false);
  const [activeSection, setActiveSection] = useState('hero');

  useEffect(() => {
    axios.get(`${API}/site-content/home`).then(r => setContent(r.data)).catch(() => {});
  }, []);

  const updateField = (section, field, value) => {
    setContent(c => ({ ...c, [section]: { ...(c?.[section] || {}), [field]: value } }));
  };

  const updateService = (idx, field, value) => {
    setContent(c => {
      const services = [...(c?.services || [])];
      services[idx] = { ...services[idx], [field]: value };
      return { ...c, services };
    });
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axiosAuth.put(`${API}/site-content/home`, content);
      toast.success(lang === 'fr' ? 'Contenu de la page d\'accueil enregistré!' : 'Home content saved!');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Error');
    } finally {
      setSaving(false);
    }
  };

  if (!content) return <div className="p-8">{lang === 'fr' ? 'Chargement...' : 'Loading...'}</div>;

  const hero = content.hero || {};
  const ctaBar = content.cta_bar || {};
  const services = content.services || [];
  const aboutPreview = content.about_preview || {};
  const quickInfo = content.quick_info || {};

  const sections = [
    { key: 'hero', label: lang === 'fr' ? 'Bannière (Hero)' : 'Hero' },
    { key: 'cta_bar', label: lang === 'fr' ? 'Barre de boutons' : 'CTA Bar' },
    { key: 'services', label: lang === 'fr' ? 'Cartes Services' : 'Services Cards' },
    { key: 'about', label: lang === 'fr' ? 'Aperçu À propos' : 'About Preview' },
    { key: 'quick', label: lang === 'fr' ? 'Infos rapides' : 'Quick Info' },
  ];

  return (
    <div data-testid="admin-home-content-page">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <h2 className="font-heading text-2xl font-bold text-[#1a1a1a]">{lang === 'fr' ? 'Page d\'accueil — Contenu' : 'Home Page — Content'}</h2>
        <div className="flex items-center gap-2">
          <a href="/" target="_blank" rel="noopener noreferrer" className="px-4 py-2 text-sm border border-[#e0e0e0] rounded-lg hover:bg-white" data-testid="preview-home-btn">
            {lang === 'fr' ? 'Voir le site' : 'View site'}
          </a>
          <button onClick={handleSave} disabled={saving} className="btn-belgo px-5 py-2 rounded-lg text-white font-semibold disabled:opacity-50" style={{ backgroundColor: '#e53a3a' }} data-testid="save-home-content">
            {saving ? '...' : (lang === 'fr' ? 'Enregistrer tout' : 'Save all')}
          </button>
        </div>
      </div>

      {/* Section tabs */}
      <div className="flex gap-2 mb-6 border-b border-[#e0e0e0] flex-wrap">
        {sections.map(s => (
          <button
            key={s.key}
            onClick={() => setActiveSection(s.key)}
            className={`px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${activeSection === s.key ? 'border-[#e53a3a] text-[#e53a3a]' : 'border-transparent text-[#555555] hover:text-[#1a1a1a]'}`}
            data-testid={`home-section-tab-${s.key}`}
          >
            {s.label}
          </button>
        ))}
      </div>

      <div className="bg-white rounded-xl border border-[#e0e0e0] p-6 space-y-5">
        {/* HERO */}
        {activeSection === 'hero' && (
          <>
            <h3 className="font-heading text-lg font-semibold">{lang === 'fr' ? 'Bannière principale' : 'Hero banner'}</h3>
            <div className="grid grid-cols-2 gap-4">
              <div><Label>Badge (FR)</Label><Input value={hero.badge_fr || ''} onChange={e => updateField('hero', 'badge_fr', e.target.value)} className="mt-1" data-testid="hero-badge-fr" /></div>
              <div><Label>Badge (EN)</Label><Input value={hero.badge_en || ''} onChange={e => updateField('hero', 'badge_en', e.target.value)} className="mt-1" data-testid="hero-badge-en" /></div>
              <div><Label>{lang === 'fr' ? 'Titre principal (FR)' : 'Main title (FR)'}</Label><Input value={hero.title_main_fr || ''} onChange={e => updateField('hero', 'title_main_fr', e.target.value)} className="mt-1" /></div>
              <div><Label>{lang === 'fr' ? 'Titre principal (EN)' : 'Main title (EN)'}</Label><Input value={hero.title_main_en || ''} onChange={e => updateField('hero', 'title_main_en', e.target.value)} className="mt-1" /></div>
              <div><Label>{lang === 'fr' ? 'Mot mis en évidence (FR, jaune)' : 'Highlighted word (FR, yellow)'}</Label><Input value={hero.title_highlight_fr || ''} onChange={e => updateField('hero', 'title_highlight_fr', e.target.value)} className="mt-1" /></div>
              <div><Label>{lang === 'fr' ? 'Mot mis en évidence (EN, jaune)' : 'Highlighted word (EN, yellow)'}</Label><Input value={hero.title_highlight_en || ''} onChange={e => updateField('hero', 'title_highlight_en', e.target.value)} className="mt-1" /></div>
              <div className="col-span-2"><Label>{lang === 'fr' ? 'Sous-titre (FR)' : 'Subtitle (FR)'}</Label><Input value={hero.subtitle_fr || ''} onChange={e => updateField('hero', 'subtitle_fr', e.target.value)} className="mt-1" /></div>
              <div className="col-span-2"><Label>{lang === 'fr' ? 'Sous-titre (EN)' : 'Subtitle (EN)'}</Label><Input value={hero.subtitle_en || ''} onChange={e => updateField('hero', 'subtitle_en', e.target.value)} className="mt-1" /></div>
              <div className="col-span-2"><Label>Tagline (FR)</Label><Textarea value={hero.tagline_fr || ''} onChange={e => updateField('hero', 'tagline_fr', e.target.value)} rows={2} className="mt-1" /></div>
              <div className="col-span-2"><Label>Tagline (EN)</Label><Textarea value={hero.tagline_en || ''} onChange={e => updateField('hero', 'tagline_en', e.target.value)} rows={2} className="mt-1" /></div>
            </div>
            <ImageUploadField value={hero.background_image} onChange={(v) => updateField('hero', 'background_image', v)} label={lang === 'fr' ? 'Image de fond (bannière)' : 'Background image (hero)'} testId="hero-bg" />
            <h4 className="font-semibold text-sm pt-4 border-t">{lang === 'fr' ? 'Boutons' : 'Buttons'}</h4>
            <div className="grid grid-cols-2 gap-4">
              <div><Label>{lang === 'fr' ? 'Bouton "Menu" (FR)' : '"Menu" button (FR)'}</Label><Input value={hero.cta_menu_fr || ''} onChange={e => updateField('hero', 'cta_menu_fr', e.target.value)} className="mt-1" /></div>
              <div><Label>{lang === 'fr' ? 'Bouton "Menu" (EN)' : '"Menu" button (EN)'}</Label><Input value={hero.cta_menu_en || ''} onChange={e => updateField('hero', 'cta_menu_en', e.target.value)} className="mt-1" /></div>
              <div><Label>{lang === 'fr' ? 'Bouton "Commander" (FR)' : '"Order" button (FR)'}</Label><Input value={hero.cta_order_fr || ''} onChange={e => updateField('hero', 'cta_order_fr', e.target.value)} className="mt-1" /></div>
              <div><Label>{lang === 'fr' ? 'Bouton "Commander" (EN)' : '"Order" button (EN)'}</Label><Input value={hero.cta_order_en || ''} onChange={e => updateField('hero', 'cta_order_en', e.target.value)} className="mt-1" /></div>
              <div><Label>{lang === 'fr' ? 'Bouton "Livraison" (FR)' : '"Delivery" button (FR)'}</Label><Input value={hero.cta_delivery_fr || ''} onChange={e => updateField('hero', 'cta_delivery_fr', e.target.value)} className="mt-1" /></div>
              <div><Label>{lang === 'fr' ? 'Bouton "Livraison" (EN)' : '"Delivery" button (EN)'}</Label><Input value={hero.cta_delivery_en || ''} onChange={e => updateField('hero', 'cta_delivery_en', e.target.value)} className="mt-1" /></div>
              <div className="col-span-2"><Label>{lang === 'fr' ? 'URL de livraison' : 'Delivery URL'}</Label><Input value={hero.delivery_url || ''} onChange={e => updateField('hero', 'delivery_url', e.target.value)} className="mt-1" placeholder="https://..." /></div>
            </div>
          </>
        )}

        {/* CTA BAR */}
        {activeSection === 'cta_bar' && (
          <>
            <h3 className="font-heading text-lg font-semibold">{lang === 'fr' ? 'Barre de boutons (sous la bannière)' : 'Button bar (below hero)'}</h3>
            <div className="grid grid-cols-2 gap-4">
              <div><Label>{lang === 'fr' ? 'Texte "Emporter" (FR)' : '"Takeout" text (FR)'}</Label><Input value={ctaBar.takeout_fr || ''} onChange={e => updateField('cta_bar', 'takeout_fr', e.target.value)} className="mt-1" /></div>
              <div><Label>{lang === 'fr' ? 'Texte "Emporter" (EN)' : '"Takeout" text (EN)'}</Label><Input value={ctaBar.takeout_en || ''} onChange={e => updateField('cta_bar', 'takeout_en', e.target.value)} className="mt-1" /></div>
              <div><Label>{lang === 'fr' ? 'Texte "Livraison" (FR)' : '"Delivery" text (FR)'}</Label><Input value={ctaBar.delivery_fr || ''} onChange={e => updateField('cta_bar', 'delivery_fr', e.target.value)} className="mt-1" /></div>
              <div><Label>{lang === 'fr' ? 'Texte "Livraison" (EN)' : '"Delivery" text (EN)'}</Label><Input value={ctaBar.delivery_en || ''} onChange={e => updateField('cta_bar', 'delivery_en', e.target.value)} className="mt-1" /></div>
              <div className="col-span-2"><Label>{lang === 'fr' ? 'URL livraison' : 'Delivery URL'}</Label><Input value={ctaBar.delivery_url || ''} onChange={e => updateField('cta_bar', 'delivery_url', e.target.value)} className="mt-1" /></div>
              <div><Label>{lang === 'fr' ? 'Texte "Foodtruck" (FR)' : '"Foodtruck" text (FR)'}</Label><Input value={ctaBar.foodtruck_fr || ''} onChange={e => updateField('cta_bar', 'foodtruck_fr', e.target.value)} className="mt-1" /></div>
              <div><Label>{lang === 'fr' ? 'Texte "Foodtruck" (EN)' : '"Foodtruck" text (EN)'}</Label><Input value={ctaBar.foodtruck_en || ''} onChange={e => updateField('cta_bar', 'foodtruck_en', e.target.value)} className="mt-1" /></div>
              <div className="col-span-2"><Label>{lang === 'fr' ? 'URL foodtruck' : 'Foodtruck URL'}</Label><Input value={ctaBar.foodtruck_url || ''} onChange={e => updateField('cta_bar', 'foodtruck_url', e.target.value)} className="mt-1" /></div>
            </div>
          </>
        )}

        {/* SERVICES */}
        {activeSection === 'services' && (
          <>
            <h3 className="font-heading text-lg font-semibold">{lang === 'fr' ? '3 Cartes Services' : '3 Service Cards'}</h3>
            {services.map((svc, idx) => (
              <div key={idx} className="border border-[#e0e0e0] rounded-lg p-4 space-y-3" data-testid={`service-edit-${idx}`}>
                <h4 className="font-semibold text-sm">{lang === 'fr' ? `Carte ${idx + 1}` : `Card ${idx + 1}`}</h4>
                <div className="grid grid-cols-2 gap-3">
                  <div><Label>Titre (FR)</Label><Input value={svc.title_fr || ''} onChange={e => updateService(idx, 'title_fr', e.target.value)} className="mt-1" /></div>
                  <div><Label>Title (EN)</Label><Input value={svc.title_en || ''} onChange={e => updateService(idx, 'title_en', e.target.value)} className="mt-1" /></div>
                  <div className="col-span-2"><Label>Description (FR)</Label><Textarea value={svc.desc_fr || ''} onChange={e => updateService(idx, 'desc_fr', e.target.value)} rows={2} className="mt-1" /></div>
                  <div className="col-span-2"><Label>Description (EN)</Label><Textarea value={svc.desc_en || ''} onChange={e => updateService(idx, 'desc_en', e.target.value)} rows={2} className="mt-1" /></div>
                  <div><Label>{lang === 'fr' ? 'Lien (URL ou /menu)' : 'Link (URL or /menu)'}</Label><Input value={svc.link || ''} onChange={e => updateService(idx, 'link', e.target.value)} className="mt-1" /></div>
                  <div className="flex items-end">
                    <label className="flex items-center gap-2 text-sm pb-2">
                      <Switch checked={!!svc.external} onCheckedChange={v => updateService(idx, 'external', v)} />
                      {lang === 'fr' ? 'Lien externe (nouvelle fenêtre)' : 'External link (new tab)'}
                    </label>
                  </div>
                </div>
                <ImageUploadField value={svc.image} onChange={(v) => updateService(idx, 'image', v)} label={lang === 'fr' ? 'Image' : 'Image'} testId={`service-${idx}-img`} />
              </div>
            ))}
          </>
        )}

        {/* ABOUT */}
        {activeSection === 'about' && (
          <>
            <h3 className="font-heading text-lg font-semibold">{lang === 'fr' ? 'Aperçu À propos' : 'About Preview'}</h3>
            <div className="grid grid-cols-2 gap-4">
              <div><Label>Titre (FR)</Label><Input value={aboutPreview.title_fr || ''} onChange={e => updateField('about_preview', 'title_fr', e.target.value)} className="mt-1" /></div>
              <div><Label>Title (EN)</Label><Input value={aboutPreview.title_en || ''} onChange={e => updateField('about_preview', 'title_en', e.target.value)} className="mt-1" /></div>
              <div className="col-span-2"><Label>Paragraphe 1 (FR)</Label><Textarea value={aboutPreview.p1_fr || ''} onChange={e => updateField('about_preview', 'p1_fr', e.target.value)} rows={3} className="mt-1" /></div>
              <div className="col-span-2"><Label>Paragraph 1 (EN)</Label><Textarea value={aboutPreview.p1_en || ''} onChange={e => updateField('about_preview', 'p1_en', e.target.value)} rows={3} className="mt-1" /></div>
              <div className="col-span-2"><Label>Paragraphe 2 (FR)</Label><Textarea value={aboutPreview.p2_fr || ''} onChange={e => updateField('about_preview', 'p2_fr', e.target.value)} rows={3} className="mt-1" /></div>
              <div className="col-span-2"><Label>Paragraph 2 (EN)</Label><Textarea value={aboutPreview.p2_en || ''} onChange={e => updateField('about_preview', 'p2_en', e.target.value)} rows={3} className="mt-1" /></div>
            </div>
            <ImageUploadField value={aboutPreview.image} onChange={(v) => updateField('about_preview', 'image', v)} label={lang === 'fr' ? 'Image' : 'Image'} testId="about-img" />
          </>
        )}

        {/* QUICK INFO */}
        {activeSection === 'quick' && (
          <>
            <h3 className="font-heading text-lg font-semibold">{lang === 'fr' ? 'Infos rapides (bas de page d\'accueil)' : 'Quick info (bottom of home)'}</h3>
            <div className="grid grid-cols-2 gap-4">
              <div><Label>{lang === 'fr' ? 'Heures (FR)' : 'Hours (FR)'}</Label><Input value={quickInfo.hours_text_fr || ''} onChange={e => updateField('quick_info', 'hours_text_fr', e.target.value)} className="mt-1" /></div>
              <div><Label>{lang === 'fr' ? 'Heures (EN)' : 'Hours (EN)'}</Label><Input value={quickInfo.hours_text_en || ''} onChange={e => updateField('quick_info', 'hours_text_en', e.target.value)} className="mt-1" /></div>
              <div className="col-span-2"><Label>{lang === 'fr' ? 'Adresse' : 'Address'}</Label><Input value={quickInfo.address_text || ''} onChange={e => updateField('quick_info', 'address_text', e.target.value)} className="mt-1" /></div>
            </div>
          </>
        )}
      </div>

      <div className="mt-6 flex justify-end">
        <button onClick={handleSave} disabled={saving} className="btn-belgo px-6 py-2.5 rounded-lg text-white font-semibold disabled:opacity-50" style={{ backgroundColor: '#e53a3a' }} data-testid="save-home-content-bottom">
          {saving ? '...' : (lang === 'fr' ? 'Enregistrer tout' : 'Save all')}
        </button>
      </div>
    </div>
  );
}

// Gallery Management
function GalleryManagement() {
  const { lang } = useLanguage();
  const [images, setImages] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState({ url: '', caption_fr: '', caption_en: '', order: 0 });
  const [uploading, setUploading] = useState(false);

  const fetchImages = useCallback(() => axios.get(`${API}/gallery`).then(r => setImages(r.data)).catch(() => {}), []);
  useEffect(() => { fetchImages(); }, [fetchImages]);

  const openNew = () => {
    setForm({ url: '', caption_fr: '', caption_en: '', order: images.length + 1 });
    setDialogOpen(true);
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const { data } = await axiosAuth.post(`${API}/upload`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      const fullUrl = `${process.env.REACT_APP_BACKEND_URL}${data.url}`;
      setForm(f => ({ ...f, url: fullUrl }));
      toast.success(lang === 'fr' ? 'Image uploadée!' : 'Image uploaded!');
    } catch (err) {
      toast.error(err?.response?.data?.detail || (lang === 'fr' ? 'Erreur upload' : 'Upload error'));
    } finally {
      setUploading(false);
    }
  };

  const handleSave = async () => {
    if (!form.url.trim()) {
      toast.error(lang === 'fr' ? 'URL ou upload requis' : 'URL or upload required');
      return;
    }
    try {
      await axiosAuth.post(`${API}/gallery`, {
        url: form.url,
        caption_fr: form.caption_fr || '',
        caption_en: form.caption_en || '',
        category: 'general',
        order: parseInt(form.order) || 0,
      });
      toast.success(lang === 'fr' ? 'Image ajoutée!' : 'Image added!');
      setDialogOpen(false);
      fetchImages();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Error');
    }
  };

  const handleDelete = async (img) => {
    if (!window.confirm(lang === 'fr' ? 'Supprimer cette photo de la galerie?' : 'Delete this photo from gallery?')) return;
    try {
      await axiosAuth.delete(`${API}/gallery/${img.id}`);
      toast.success(lang === 'fr' ? 'Supprimée' : 'Deleted');
      fetchImages();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Error');
    }
  };

  return (
    <div data-testid="admin-gallery-page">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <h2 className="font-heading text-2xl font-bold text-[#1a1a1a]">{lang === 'fr' ? 'Galerie photos' : 'Photo Gallery'} <span className="text-sm font-normal text-[#555555]">({images.length})</span></h2>
        <button onClick={openNew} className="btn-belgo flex items-center gap-2 px-4 py-2 rounded-lg text-white text-sm font-semibold" style={{ backgroundColor: '#e53a3a' }} data-testid="add-gallery-image">
          <Plus className="w-4 h-4" /> {lang === 'fr' ? 'Ajouter une photo' : 'Add a photo'}
        </button>
      </div>

      {/* Grid */}
      {images.length === 0 ? (
        <div className="bg-white rounded-xl border border-[#e0e0e0] p-12 text-center text-[#555555]">
          {lang === 'fr' ? 'Aucune photo dans la galerie' : 'No photos in gallery'}
        </div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4" data-testid="gallery-grid">
          {images.map(img => (
            <div key={img.id} className="group relative bg-white rounded-xl border border-[#e0e0e0] overflow-hidden" data-testid={`gallery-img-${img.id}`}>
              <div className="aspect-square overflow-hidden bg-[#f5f5f0]">
                {img.url ? (
                  <img src={img.url} alt={img.caption_fr || ''} className="w-full h-full object-cover" loading="lazy" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-[#bbb]"><Image className="w-8 h-8" /></div>
                )}
              </div>
              <div className="p-3">
                <p className="text-xs text-[#1a1a1a] line-clamp-1 font-medium">{lang === 'fr' ? img.caption_fr : img.caption_en}</p>
                <p className="text-xs text-[#888]">{lang === 'fr' ? img.caption_en : img.caption_fr}</p>
              </div>
              <button
                onClick={() => handleDelete(img)}
                className="absolute top-2 right-2 p-2 bg-white/90 hover:bg-red-600 hover:text-white text-red-600 rounded-lg shadow-md opacity-0 group-hover:opacity-100 transition-opacity"
                data-testid={`delete-gallery-${img.id}`}
                title={lang === 'fr' ? 'Supprimer' : 'Delete'}
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Add dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading">{lang === 'fr' ? 'Ajouter une photo' : 'Add a photo'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>{lang === 'fr' ? 'Image (upload ou URL)' : 'Image (upload or URL)'}</Label>
              <Input value={form.url} onChange={e => setForm(f => ({ ...f, url: e.target.value }))} placeholder="https://..." className="mt-1" data-testid="gallery-form-url" />
              <input type="file" accept="image/*" onChange={handleFileUpload} disabled={uploading} className="mt-2 text-xs" data-testid="gallery-form-file" />
              {uploading && <p className="text-xs text-[#555555] mt-1">{lang === 'fr' ? 'Téléchargement...' : 'Uploading...'}</p>}
              {form.url && <img src={form.url} alt="preview" className="mt-2 w-full max-h-48 object-cover rounded border" />}
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>{lang === 'fr' ? 'Légende (FR)' : 'Caption (FR)'}</Label>
                <Input value={form.caption_fr} onChange={e => setForm(f => ({ ...f, caption_fr: e.target.value }))} className="mt-1" data-testid="gallery-form-caption-fr" />
              </div>
              <div>
                <Label>{lang === 'fr' ? 'Légende (EN)' : 'Caption (EN)'}</Label>
                <Input value={form.caption_en} onChange={e => setForm(f => ({ ...f, caption_en: e.target.value }))} className="mt-1" data-testid="gallery-form-caption-en" />
              </div>
              <div className="col-span-2">
                <Label>{lang === 'fr' ? 'Ordre d\'affichage' : 'Display order'}</Label>
                <Input type="number" value={form.order} onChange={e => setForm(f => ({ ...f, order: e.target.value }))} className="mt-1" data-testid="gallery-form-order" />
              </div>
            </div>
            <button onClick={handleSave} className="btn-belgo w-full px-4 py-2.5 rounded-lg text-white font-semibold" style={{ backgroundColor: '#e53a3a' }} data-testid="gallery-form-save">
              {lang === 'fr' ? 'Ajouter à la galerie' : 'Add to gallery'}
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// UEat External Ordering Settings
function UEatSettings() {
  const { lang } = useLanguage();
  const [settings, setSettings] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    axios.get(`${API}/site-content/ueat`).then(r => setSettings(r.data)).catch(() => {});
  }, []);

  const update = (field, value) => setSettings(s => ({ ...s, [field]: value }));

  const handleSave = async () => {
    if (settings.enabled && !settings.url?.trim()) {
      toast.error(lang === 'fr' ? 'URL UEat requise pour activer' : 'UEat URL required to enable');
      return;
    }
    setSaving(true);
    try {
      await axiosAuth.put(`${API}/site-content/ueat`, settings);
      toast.success(lang === 'fr' ? 'Paramètres UEat enregistrés!' : 'UEat settings saved!');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Error');
    } finally {
      setSaving(false);
    }
  };

  if (!settings) return <div className="p-8">{lang === 'fr' ? 'Chargement...' : 'Loading...'}</div>;

  return (
    <div data-testid="admin-ueat-page">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div>
          <h2 className="font-heading text-2xl font-bold text-[#1a1a1a]">UEat — {lang === 'fr' ? 'Commandes externes' : 'External ordering'}</h2>
          <p className="text-sm text-[#555555] mt-1">{lang === 'fr' ? 'Activez UEat pour rediriger temporairement les commandes pour emporter vers votre page UEat.' : 'Enable UEat to temporarily redirect takeout orders to your UEat page.'}</p>
        </div>
        <button onClick={handleSave} disabled={saving} className="btn-belgo px-5 py-2 rounded-lg text-white font-semibold disabled:opacity-50" style={{ backgroundColor: '#e53a3a' }} data-testid="save-ueat-settings">
          {saving ? '...' : (lang === 'fr' ? 'Enregistrer' : 'Save')}
        </button>
      </div>

      <div className="bg-white rounded-xl border border-[#e0e0e0] p-6 space-y-5 max-w-2xl">
        {/* Toggle */}
        <div className={`flex items-center justify-between p-4 rounded-lg border-2 ${settings.enabled ? 'border-[#e53a3a] bg-red-50' : 'border-[#e0e0e0] bg-[#f5f5f0]'}`}>
          <div>
            <p className="font-semibold text-[#1a1a1a]">{lang === 'fr' ? 'Rediriger les commandes vers UEat' : 'Redirect orders to UEat'}</p>
            <p className="text-xs text-[#555555] mt-1">
              {settings.enabled
                ? (lang === 'fr' ? '✓ Actif — Les boutons "Commander pour emporter" pointent vers UEat' : '✓ Active — "Order for Takeout" buttons point to UEat')
                : (lang === 'fr' ? 'Inactif — Commandes gérées en interne' : 'Inactive — Orders handled internally')}
            </p>
          </div>
          <Switch
            checked={!!settings.enabled}
            onCheckedChange={v => update('enabled', v)}
            data-testid="ueat-enabled-toggle"
          />
        </div>

        {/* URL */}
        <div>
          <Label>{lang === 'fr' ? 'URL UEat (lien public de commande)' : 'UEat URL (public ordering link)'}</Label>
          <Input
            value={settings.url || ''}
            onChange={e => update('url', e.target.value)}
            placeholder="https://commander.ueat.io/votre-restaurant"
            className="mt-1"
            data-testid="ueat-url"
          />
          <p className="text-xs text-[#555555] mt-1">
            {lang === 'fr' ? 'Exemple: https://commander.ueat.io/belgo' : 'Example: https://order.ueat.io/belgo'}
          </p>
        </div>

        {/* Labels */}
        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label>{lang === 'fr' ? 'Texte du bouton (FR)' : 'Button label (FR)'}</Label>
            <Input value={settings.label_fr || ''} onChange={e => update('label_fr', e.target.value)} className="mt-1" data-testid="ueat-label-fr" />
          </div>
          <div>
            <Label>{lang === 'fr' ? 'Texte du bouton (EN)' : 'Button label (EN)'}</Label>
            <Input value={settings.label_en || ''} onChange={e => update('label_en', e.target.value)} className="mt-1" data-testid="ueat-label-en" />
          </div>
        </div>

        {/* Banner message */}
        <div>
          <Label>{lang === 'fr' ? 'Message de bannière (FR)' : 'Banner message (FR)'}</Label>
          <Textarea value={settings.banner_message_fr || ''} onChange={e => update('banner_message_fr', e.target.value)} rows={2} className="mt-1" data-testid="ueat-banner-fr" />
        </div>
        <div>
          <Label>{lang === 'fr' ? 'Message de bannière (EN)' : 'Banner message (EN)'}</Label>
          <Textarea value={settings.banner_message_en || ''} onChange={e => update('banner_message_en', e.target.value)} rows={2} className="mt-1" data-testid="ueat-banner-en" />
        </div>

        {/* Redirect Menu Link toggle */}
        <div className={`flex items-center justify-between p-4 rounded-lg border-2 ${settings.redirect_menu_link ? 'border-[#f4d00d] bg-yellow-50' : 'border-[#e0e0e0] bg-[#f5f5f0]'}`}>
          <div>
            <p className="font-semibold text-[#1a1a1a]">{lang === 'fr' ? 'Rediriger le lien "Menu" vers UEat' : 'Redirect "Menu" link to UEat'}</p>
            <p className="text-xs text-[#555555] mt-1">
              {settings.redirect_menu_link
                ? (lang === 'fr' ? '✓ Le lien Menu du header et les boutons "Voir le menu" pointent vers UEat' : '✓ Header Menu link and "View menu" buttons point to UEat')
                : (lang === 'fr' ? 'Inactif — Le lien Menu pointe vers la page interne /menu' : 'Inactive — Menu link points to internal /menu page')}
            </p>
          </div>
          <Switch
            checked={!!settings.redirect_menu_link}
            onCheckedChange={v => update('redirect_menu_link', v)}
            data-testid="ueat-redirect-menu-toggle"
          />
        </div>

        {/* Preview */}
        {settings.url && (
          <div className="pt-4 border-t border-[#e0e0e0]">
            <p className="text-xs text-[#555555] mb-2">{lang === 'fr' ? 'Aperçu du bouton :' : 'Button preview:'}</p>
            <a
              href={settings.url}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg font-bold text-white bg-[#e53a3a] hover:bg-[#c62828] transition-colors"
              data-testid="ueat-preview-btn"
            >
              <ExternalLink className="w-4 h-4" /> {lang === 'fr' ? settings.label_fr : settings.label_en}
            </a>
          </div>
        )}
      </div>
    </div>
  );
}

// Ordering Toggles Admin (Takeout / Delivery ON-OFF)
function OrderingTogglesAdmin() {
  const { lang } = useLanguage();
  const [settings, setSettings] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    axios.get(`${API}/settings/ordering`).then(r => setSettings(r.data)).catch(() => {});
  }, []);

  const update = (field, value) => setSettings(s => ({ ...s, [field]: value }));

  const handleSave = async () => {
    setSaving(true);
    try {
      await axiosAuth.put(`${API}/settings/ordering`, settings);
      toast.success(lang === 'fr' ? 'Paramètres enregistrés!' : 'Settings saved!');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Error');
    } finally {
      setSaving(false);
    }
  };

  if (!settings) return <div className="p-8">{lang === 'fr' ? 'Chargement...' : 'Loading...'}</div>;

  return (
    <div data-testid="admin-ordering-page">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div>
          <h2 className="font-heading text-2xl font-bold text-[#1a1a1a]">{lang === 'fr' ? 'Commandes en ligne — ON/OFF' : 'Online orders — ON/OFF'}</h2>
          <p className="text-sm text-[#555555] mt-1">{lang === 'fr' ? 'Activer ou désactiver les commandes pour emporter et la livraison.' : 'Enable or disable takeout orders and delivery.'}</p>
        </div>
        <button onClick={handleSave} disabled={saving} className="btn-belgo px-5 py-2 rounded-lg text-white font-semibold disabled:opacity-50" style={{ backgroundColor: '#e53a3a' }} data-testid="save-ordering-settings">
          {saving ? '...' : (lang === 'fr' ? 'Enregistrer' : 'Save')}
        </button>
      </div>

      <div className="bg-white rounded-xl border border-[#e0e0e0] p-6 space-y-4 max-w-2xl">
        <div className={`flex items-center justify-between p-4 rounded-lg border-2 ${settings.takeout_enabled ? 'border-green-500 bg-green-50' : 'border-[#e0e0e0] bg-[#f5f5f0]'}`}>
          <div>
            <p className="font-semibold text-[#1a1a1a]">{lang === 'fr' ? 'Commandes pour emporter (interne)' : 'Takeout orders (internal)'}</p>
            <p className="text-xs text-[#555555] mt-1">
              {settings.takeout_enabled
                ? (lang === 'fr' ? '✓ Actif — Les clients peuvent passer commande via le site' : '✓ Active — Customers can place orders via the site')
                : (lang === 'fr' ? '✗ Inactif — Bouton "Commander" caché, panier désactivé' : '✗ Inactive — "Order" button hidden, cart disabled')}
            </p>
          </div>
          <Switch checked={!!settings.takeout_enabled} onCheckedChange={v => update('takeout_enabled', v)} data-testid="takeout-toggle" />
        </div>

        <div className={`flex items-center justify-between p-4 rounded-lg border-2 ${settings.delivery_enabled ? 'border-green-500 bg-green-50' : 'border-[#e0e0e0] bg-[#f5f5f0]'}`}>
          <div>
            <p className="font-semibold text-[#1a1a1a]">{lang === 'fr' ? 'Livraison (SkipTheDishes)' : 'Delivery (SkipTheDishes)'}</p>
            <p className="text-xs text-[#555555] mt-1">
              {settings.delivery_enabled
                ? (lang === 'fr' ? '✓ Actif — Bouton "Livraison" visible' : '✓ Active — "Delivery" button visible')
                : (lang === 'fr' ? '✗ Inactif — Bouton "Livraison" caché' : '✗ Inactive — "Delivery" button hidden')}
            </p>
          </div>
          <Switch checked={!!settings.delivery_enabled} onCheckedChange={v => update('delivery_enabled', v)} data-testid="delivery-toggle" />
        </div>

        <div>
          <Label>{lang === 'fr' ? 'URL de livraison' : 'Delivery URL'}</Label>
          <Input value={settings.delivery_url || ''} onChange={e => update('delivery_url', e.target.value)} placeholder="https://..." className="mt-1" data-testid="ordering-delivery-url" />
        </div>

        <div>
          <Label>{lang === 'fr' ? 'Message quand désactivé (FR)' : 'Disabled message (FR)'}</Label>
          <Textarea value={settings.disabled_message_fr || ''} onChange={e => update('disabled_message_fr', e.target.value)} rows={2} className="mt-1" data-testid="ordering-msg-fr" />
        </div>
        <div>
          <Label>{lang === 'fr' ? 'Message quand désactivé (EN)' : 'Disabled message (EN)'}</Label>
          <Textarea value={settings.disabled_message_en || ''} onChange={e => update('disabled_message_en', e.target.value)} rows={2} className="mt-1" data-testid="ordering-msg-en" />
        </div>
      </div>
    </div>
  );
}

// Jobs Admin (Career postings + applications)
function JobsAdmin() {
  const { lang } = useLanguage();
  const [tab, setTab] = useState('postings');
  const [jobs, setJobs] = useState([]);
  const [applications, setApplications] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editJob, setEditJob] = useState(null);
  const [form, setForm] = useState({ title_fr: '', title_en: '', description_fr: '', description_en: '', location_fr: '', location_en: '', type_fr: '', type_en: '', active: true, order: 0 });

  const fetchAll = useCallback(async () => {
    const [j, a] = await Promise.all([
      axios.get(`${API}/jobs?active_only=false`).catch(() => ({ data: [] })),
      axiosAuth.get(`${API}/jobs/applications`).catch(() => ({ data: [] })),
    ]);
    setJobs(j.data);
    setApplications(a.data);
  }, []);
  useEffect(() => { fetchAll(); }, [fetchAll]);

  const openNew = () => {
    setEditJob(null);
    setForm({ title_fr: '', title_en: '', description_fr: '', description_en: '', location_fr: '153 Champlain, Saint-Jean-sur-Richelieu', location_en: '153 Champlain, Saint-Jean-sur-Richelieu', type_fr: 'Temps plein', type_en: 'Full-time', active: true, order: jobs.length + 1 });
    setDialogOpen(true);
  };

  const openEdit = (job) => {
    setEditJob(job);
    setForm({ ...job, order: job.order || 0 });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!form.title_fr.trim() || !form.title_en.trim()) {
      toast.error(lang === 'fr' ? 'Titres FR et EN requis' : 'FR and EN titles required');
      return;
    }
    const payload = {
      title_fr: form.title_fr, title_en: form.title_en,
      description_fr: form.description_fr || '', description_en: form.description_en || '',
      location_fr: form.location_fr || '', location_en: form.location_en || '',
      type_fr: form.type_fr || '', type_en: form.type_en || '',
      active: !!form.active, order: parseInt(form.order) || 0,
    };
    try {
      if (editJob) {
        await axiosAuth.put(`${API}/jobs/${editJob.id}`, payload);
      } else {
        await axiosAuth.post(`${API}/jobs`, payload);
      }
      toast.success(lang === 'fr' ? 'Enregistré!' : 'Saved!');
      setDialogOpen(false);
      fetchAll();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Error');
    }
  };

  const handleDelete = async (job) => {
    if (!window.confirm(lang === 'fr' ? `Supprimer "${job.title_fr}"?` : `Delete "${job.title_en}"?`)) return;
    await axiosAuth.delete(`${API}/jobs/${job.id}`);
    toast.success(lang === 'fr' ? 'Supprimé' : 'Deleted');
    fetchAll();
  };

  const handleDeleteApp = async (app) => {
    if (!window.confirm(lang === 'fr' ? 'Supprimer cette candidature?' : 'Delete this application?')) return;
    await axiosAuth.delete(`${API}/jobs/applications/${app.id}`);
    toast.success(lang === 'fr' ? 'Supprimée' : 'Deleted');
    fetchAll();
  };

  return (
    <div data-testid="admin-jobs-page">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <h2 className="font-heading text-2xl font-bold text-[#1a1a1a]">{lang === 'fr' ? 'Emplois & Candidatures' : 'Jobs & Applications'}</h2>
        {tab === 'postings' && (
          <button onClick={openNew} className="btn-belgo flex items-center gap-2 px-4 py-2 rounded-lg text-white text-sm font-semibold" style={{ backgroundColor: '#e53a3a' }} data-testid="add-job-btn">
            <Plus className="w-4 h-4" /> {lang === 'fr' ? 'Ajouter un poste' : 'Add a job'}
          </button>
        )}
      </div>

      <div className="flex gap-2 mb-6 border-b border-[#e0e0e0]">
        <button onClick={() => setTab('postings')} className={`px-5 py-2.5 text-sm font-medium border-b-2 ${tab === 'postings' ? 'border-[#e53a3a] text-[#e53a3a]' : 'border-transparent text-[#555555]'}`} data-testid="jobs-tab-postings">
          {lang === 'fr' ? 'Postes' : 'Postings'} ({jobs.length})
        </button>
        <button onClick={() => setTab('applications')} className={`px-5 py-2.5 text-sm font-medium border-b-2 ${tab === 'applications' ? 'border-[#e53a3a] text-[#e53a3a]' : 'border-transparent text-[#555555]'}`} data-testid="jobs-tab-applications">
          {lang === 'fr' ? 'Candidatures' : 'Applications'} ({applications.length})
        </button>
      </div>

      {tab === 'postings' && (
        <div className="space-y-3">
          {jobs.length === 0 ? (
            <p className="text-center py-8 text-[#555555]">{lang === 'fr' ? 'Aucun poste publié' : 'No jobs posted'}</p>
          ) : jobs.map(job => (
            <div key={job.id} className="bg-white rounded-xl border border-[#e0e0e0] p-4 flex items-center gap-4 flex-wrap" data-testid={`job-row-${job.id}`}>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <h4 className="font-semibold text-[#1a1a1a] truncate">{lang === 'fr' ? job.title_fr : job.title_en}</h4>
                  {job.active ? <Badge className="text-xs bg-green-100 text-green-800 border border-green-300">{lang === 'fr' ? 'Actif' : 'Active'}</Badge> : <Badge variant="outline" className="text-xs">{lang === 'fr' ? 'Inactif' : 'Inactive'}</Badge>}
                </div>
                <p className="text-xs text-[#555555] mt-1">{lang === 'fr' ? job.type_fr : job.type_en} · {lang === 'fr' ? job.location_fr : job.location_en}</p>
              </div>
              <button onClick={() => openEdit(job)} className="px-3 py-1.5 text-xs rounded hover:bg-[#f5f5f0] text-[#555555]" data-testid={`edit-job-${job.id}`}>
                {lang === 'fr' ? 'Modifier' : 'Edit'}
              </button>
              <button onClick={() => handleDelete(job)} className="px-2 py-1.5 text-xs rounded hover:bg-red-50 text-red-600" data-testid={`delete-job-${job.id}`}>
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          ))}
        </div>
      )}

      {tab === 'applications' && (
        <div className="space-y-3">
          {applications.length === 0 ? (
            <p className="text-center py-8 text-[#555555]">{lang === 'fr' ? 'Aucune candidature reçue' : 'No applications received'}</p>
          ) : applications.map(app => {
            const job = jobs.find(j => j.id === app.job_id);
            return (
              <div key={app.id} className="bg-white rounded-xl border border-[#e0e0e0] p-4" data-testid={`app-row-${app.id}`}>
                <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
                  <h4 className="font-semibold text-[#1a1a1a]">{app.name}</h4>
                  <button onClick={() => handleDeleteApp(app)} className="px-2 py-1 text-xs rounded hover:bg-red-50 text-red-600">
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
                <p className="text-xs text-[#555555]">
                  <a href={`mailto:${app.email}`} className="text-[#e53a3a] hover:underline">{app.email}</a>
                  {app.phone && <span> · {app.phone}</span>}
                </p>
                <p className="text-xs text-[#888] mt-1">{lang === 'fr' ? 'Poste' : 'Position'}: {job ? (lang === 'fr' ? job.title_fr : job.title_en) : (app.job_id || (lang === 'fr' ? 'Candidature générale' : 'General application'))}</p>
                {app.cv_url && (
                  <a href={app.cv_url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-xs text-[#e53a3a] hover:underline mt-1" data-testid={`app-cv-link-${app.id}`}>
                    📎 {app.cv_filename || (lang === 'fr' ? 'Télécharger le CV' : 'Download CV')}
                  </a>
                )}
                {app.message && <p className="text-sm mt-2 italic">"{app.message}"</p>}
                <p className="text-xs text-[#aaa] mt-2">{app.created_at?.substring(0, 16)}</p>
              </div>
            );
          })}
        </div>
      )}

      {/* Job dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading">{editJob ? (lang === 'fr' ? 'Modifier le poste' : 'Edit job') : (lang === 'fr' ? 'Nouveau poste' : 'New job')}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Titre (FR)</Label><Input value={form.title_fr} onChange={e => setForm(f => ({ ...f, title_fr: e.target.value }))} className="mt-1" data-testid="job-form-title-fr" /></div>
              <div><Label>Title (EN)</Label><Input value={form.title_en} onChange={e => setForm(f => ({ ...f, title_en: e.target.value }))} className="mt-1" data-testid="job-form-title-en" /></div>
              <div><Label>{lang === 'fr' ? 'Type (FR)' : 'Type (FR)'}</Label><Input value={form.type_fr} onChange={e => setForm(f => ({ ...f, type_fr: e.target.value }))} placeholder="Temps plein" className="mt-1" /></div>
              <div><Label>{lang === 'fr' ? 'Type (EN)' : 'Type (EN)'}</Label><Input value={form.type_en} onChange={e => setForm(f => ({ ...f, type_en: e.target.value }))} placeholder="Full-time" className="mt-1" /></div>
              <div className="col-span-2"><Label>{lang === 'fr' ? 'Lieu (FR)' : 'Location (FR)'}</Label><Input value={form.location_fr} onChange={e => setForm(f => ({ ...f, location_fr: e.target.value }))} className="mt-1" /></div>
              <div className="col-span-2"><Label>{lang === 'fr' ? 'Lieu (EN)' : 'Location (EN)'}</Label><Input value={form.location_en} onChange={e => setForm(f => ({ ...f, location_en: e.target.value }))} className="mt-1" /></div>
              <div className="col-span-2"><Label>Description (FR)</Label><Textarea value={form.description_fr} onChange={e => setForm(f => ({ ...f, description_fr: e.target.value }))} rows={4} className="mt-1" /></div>
              <div className="col-span-2"><Label>Description (EN)</Label><Textarea value={form.description_en} onChange={e => setForm(f => ({ ...f, description_en: e.target.value }))} rows={4} className="mt-1" /></div>
              <div><Label>{lang === 'fr' ? 'Ordre' : 'Order'}</Label><Input type="number" value={form.order} onChange={e => setForm(f => ({ ...f, order: e.target.value }))} className="mt-1" /></div>
              <div className="flex items-end pb-2">
                <label className="flex items-center gap-2 text-sm">
                  <Switch checked={!!form.active} onCheckedChange={v => setForm(f => ({ ...f, active: v }))} />
                  {lang === 'fr' ? 'Actif (visible)' : 'Active (visible)'}
                </label>
              </div>
            </div>
            <button onClick={handleSave} className="btn-belgo w-full px-4 py-2.5 rounded-lg text-white font-semibold" style={{ backgroundColor: '#e53a3a' }} data-testid="job-form-save">
              {lang === 'fr' ? 'Enregistrer' : 'Save'}
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// Retailers Admin (Points of sale for products)
function RetailersAdmin() {
  const { lang } = useLanguage();
  const [locations, setLocations] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editLoc, setEditLoc] = useState(null);
  const [form, setForm] = useState({ product_type: 'mayo', product_name_fr: '', product_name_en: '', retailer_name: '', address: '', city: '', phone: '', website: '', active: true, order: 0 });
  const [filter, setFilter] = useState('all');

  const PRODUCT_TYPES = [
    { key: 'mayo', label_fr: 'Mayo épicée', label_en: 'Spicy Mayo' },
    { key: 'bbq', label_fr: 'Sauce BBQ', label_en: 'BBQ Sauce' },
    { key: 'poutine_sauce', label_fr: 'Sauce à poutine', label_en: 'Poutine Gravy' },
    { key: 'other', label_fr: 'Autre', label_en: 'Other' },
  ];

  const fetchAll = useCallback(() => {
    axios.get(`${API}/retail-locations?active_only=false`).then(r => setLocations(r.data)).catch(() => {});
  }, []);
  useEffect(() => { fetchAll(); }, [fetchAll]);

  const openNew = () => {
    setEditLoc(null);
    setForm({ product_type: 'mayo', product_name_fr: '', product_name_en: '', retailer_name: '', address: '', city: '', phone: '', website: '', active: true, order: locations.length + 1 });
    setDialogOpen(true);
  };

  const openEdit = (loc) => {
    setEditLoc(loc);
    setForm({ ...loc, order: loc.order || 0 });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!form.retailer_name.trim()) {
      toast.error(lang === 'fr' ? 'Nom du détaillant requis' : 'Retailer name required');
      return;
    }
    const payload = {
      product_type: form.product_type,
      product_name_fr: form.product_name_fr || '',
      product_name_en: form.product_name_en || '',
      retailer_name: form.retailer_name.trim(),
      address: form.address || '',
      city: form.city || '',
      phone: form.phone || '',
      website: form.website || '',
      active: !!form.active,
      order: parseInt(form.order) || 0,
    };
    try {
      if (editLoc) {
        await axiosAuth.put(`${API}/retail-locations/${editLoc.id}`, payload);
      } else {
        await axiosAuth.post(`${API}/retail-locations`, payload);
      }
      toast.success(lang === 'fr' ? 'Enregistré!' : 'Saved!');
      setDialogOpen(false);
      fetchAll();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Error');
    }
  };

  const handleDelete = async (loc) => {
    if (!window.confirm(lang === 'fr' ? `Supprimer "${loc.retailer_name}"?` : `Delete "${loc.retailer_name}"?`)) return;
    await axiosAuth.delete(`${API}/retail-locations/${loc.id}`);
    toast.success(lang === 'fr' ? 'Supprimé' : 'Deleted');
    fetchAll();
  };

  const filtered = filter === 'all' ? locations : locations.filter(l => l.product_type === filter);
  const labelFor = (key) => {
    const p = PRODUCT_TYPES.find(x => x.key === key);
    return p ? (lang === 'fr' ? p.label_fr : p.label_en) : key;
  };

  return (
    <div data-testid="admin-retailers-page">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <h2 className="font-heading text-2xl font-bold text-[#1a1a1a]">{lang === 'fr' ? 'Points de vente' : 'Retailers'} <span className="text-sm font-normal text-[#555555]">({locations.length})</span></h2>
        <button onClick={openNew} className="btn-belgo flex items-center gap-2 px-4 py-2 rounded-lg text-white text-sm font-semibold" style={{ backgroundColor: '#e53a3a' }} data-testid="add-retailer-btn">
          <Plus className="w-4 h-4" /> {lang === 'fr' ? 'Ajouter un point de vente' : 'Add retailer'}
        </button>
      </div>

      {/* Filter tabs */}
      <div className="flex gap-2 mb-6 flex-wrap">
        <button onClick={() => setFilter('all')} className={`px-4 py-2 rounded-lg text-sm font-medium ${filter === 'all' ? 'bg-[#e53a3a] text-white' : 'bg-white border border-[#e0e0e0] text-[#555555]'}`} data-testid="filter-all">
          {lang === 'fr' ? 'Tous' : 'All'} ({locations.length})
        </button>
        {PRODUCT_TYPES.map(p => {
          const count = locations.filter(l => l.product_type === p.key).length;
          return (
            <button key={p.key} onClick={() => setFilter(p.key)} className={`px-4 py-2 rounded-lg text-sm font-medium ${filter === p.key ? 'bg-[#e53a3a] text-white' : 'bg-white border border-[#e0e0e0] text-[#555555]'}`} data-testid={`filter-${p.key}`}>
              {lang === 'fr' ? p.label_fr : p.label_en} ({count})
            </button>
          );
        })}
      </div>

      {/* List */}
      {filtered.length === 0 ? (
        <div className="bg-white rounded-xl border border-[#e0e0e0] p-12 text-center text-[#555555]">
          {lang === 'fr' ? 'Aucun point de vente' : 'No retailers'}
        </div>
      ) : (
        <div className="space-y-2">
          {filtered.map(loc => (
            <div key={loc.id} className="bg-white rounded-xl border border-[#e0e0e0] p-4 flex items-center gap-4 flex-wrap" data-testid={`retailer-row-${loc.id}`}>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <h4 className="font-semibold text-[#1a1a1a] truncate">{loc.retailer_name}</h4>
                  <Badge variant="outline" className="text-xs">{labelFor(loc.product_type)}</Badge>
                  {!loc.active && <Badge variant="outline" className="text-xs">{lang === 'fr' ? 'Inactif' : 'Inactive'}</Badge>}
                </div>
                <p className="text-xs text-[#555555] mt-1">
                  {[loc.address, loc.city].filter(Boolean).join(', ') || '—'}
                  {loc.phone && <span> · 📞 {loc.phone}</span>}
                </p>
              </div>
              <button onClick={() => openEdit(loc)} className="px-3 py-1.5 text-xs rounded hover:bg-[#f5f5f0] text-[#555555]" data-testid={`edit-retailer-${loc.id}`}>
                {lang === 'fr' ? 'Modifier' : 'Edit'}
              </button>
              <button onClick={() => handleDelete(loc)} className="px-2 py-1.5 text-xs rounded hover:bg-red-50 text-red-600" data-testid={`delete-retailer-${loc.id}`}>
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading">{editLoc ? (lang === 'fr' ? 'Modifier' : 'Edit') : (lang === 'fr' ? 'Nouveau point de vente' : 'New retailer')}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>{lang === 'fr' ? 'Produit' : 'Product'}</Label>
              <select value={form.product_type} onChange={e => setForm(f => ({ ...f, product_type: e.target.value }))} className="mt-1 w-full rounded-md border border-[#e0e0e0] px-3 py-2 text-sm bg-white" data-testid="retailer-form-product">
                {PRODUCT_TYPES.map(p => <option key={p.key} value={p.key}>{lang === 'fr' ? p.label_fr : p.label_en}</option>)}
              </select>
            </div>
            {form.product_type === 'other' && (
              <div className="grid grid-cols-2 gap-3">
                <div><Label>{lang === 'fr' ? 'Nom du produit (FR)' : 'Product name (FR)'}</Label><Input value={form.product_name_fr} onChange={e => setForm(f => ({ ...f, product_name_fr: e.target.value }))} className="mt-1" /></div>
                <div><Label>{lang === 'fr' ? 'Nom du produit (EN)' : 'Product name (EN)'}</Label><Input value={form.product_name_en} onChange={e => setForm(f => ({ ...f, product_name_en: e.target.value }))} className="mt-1" /></div>
              </div>
            )}
            <div>
              <Label>{lang === 'fr' ? 'Nom du détaillant' : 'Retailer name'} *</Label>
              <Input value={form.retailer_name} onChange={e => setForm(f => ({ ...f, retailer_name: e.target.value }))} placeholder="Ex: IGA, Métro, Provigo..." className="mt-1" data-testid="retailer-form-name" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>{lang === 'fr' ? 'Adresse' : 'Address'}</Label><Input value={form.address} onChange={e => setForm(f => ({ ...f, address: e.target.value }))} className="mt-1" /></div>
              <div><Label>{lang === 'fr' ? 'Ville' : 'City'}</Label><Input value={form.city} onChange={e => setForm(f => ({ ...f, city: e.target.value }))} className="mt-1" /></div>
              <div><Label>{lang === 'fr' ? 'Téléphone' : 'Phone'}</Label><Input value={form.phone} onChange={e => setForm(f => ({ ...f, phone: e.target.value }))} className="mt-1" /></div>
              <div><Label>Site web</Label><Input value={form.website} onChange={e => setForm(f => ({ ...f, website: e.target.value }))} placeholder="https://..." className="mt-1" /></div>
              <div><Label>{lang === 'fr' ? 'Ordre' : 'Order'}</Label><Input type="number" value={form.order} onChange={e => setForm(f => ({ ...f, order: e.target.value }))} className="mt-1" /></div>
              <div className="flex items-end pb-2">
                <label className="flex items-center gap-2 text-sm">
                  <Switch checked={!!form.active} onCheckedChange={v => setForm(f => ({ ...f, active: v }))} />
                  {lang === 'fr' ? 'Actif (visible)' : 'Active (visible)'}
                </label>
              </div>
            </div>
            <button onClick={handleSave} className="btn-belgo w-full px-4 py-2.5 rounded-lg text-white font-semibold" style={{ backgroundColor: '#e53a3a' }} data-testid="retailer-form-save">
              {lang === 'fr' ? 'Enregistrer' : 'Save'}
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// Theme Admin (Font + Colors + Global address/phone/email)
function ThemeAdmin() {
  const { lang } = useLanguage();
  const [theme, setTheme] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    axios.get(`${API}/site-content/theme`).then(r => setTheme(r.data)).catch(() => {});
  }, []);

  const update = (field, value) => setTheme(t => ({ ...t, [field]: value }));

  const FONTS = ['Inter', 'Poppins', 'Montserrat', 'Roboto', 'Open Sans', 'Lato', 'Raleway', 'Nunito', 'Playfair Display', 'Merriweather'];

  const handleSave = async () => {
    setSaving(true);
    try {
      await axiosAuth.put(`${API}/site-content/theme`, theme);
      toast.success(lang === 'fr' ? 'Apparence enregistrée! Rechargez pour voir tous les changements.' : 'Theme saved! Reload to see all changes.');
      setTimeout(() => window.location.reload(), 800);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Error');
    } finally {
      setSaving(false);
    }
  };

  if (!theme) return <div className="p-8">{lang === 'fr' ? 'Chargement...' : 'Loading...'}</div>;

  return (
    <div data-testid="admin-theme-page">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div>
          <h2 className="font-heading text-2xl font-bold text-[#1a1a1a]">{lang === 'fr' ? 'Apparence du site' : 'Site appearance'}</h2>
          <p className="text-sm text-[#555555] mt-1">{lang === 'fr' ? 'Police, couleurs et coordonnées globales (affichées sur tout le site).' : 'Font, colors and global contact info (shown across the site).'}</p>
        </div>
        <button onClick={handleSave} disabled={saving} className="btn-belgo px-5 py-2 rounded-lg text-white font-semibold disabled:opacity-50" style={{ backgroundColor: '#e53a3a' }} data-testid="save-theme">
          {saving ? '...' : (lang === 'fr' ? 'Enregistrer & recharger' : 'Save & reload')}
        </button>
      </div>

      <div className="bg-white rounded-xl border border-[#e0e0e0] p-6 space-y-5 max-w-2xl">
        <div>
          <Label>{lang === 'fr' ? 'Police globale' : 'Global font'}</Label>
          <select value={theme.font_family || 'Inter'} onChange={e => update('font_family', e.target.value)} className="mt-1 w-full rounded-md border border-[#e0e0e0] px-3 py-2 text-sm bg-white" data-testid="theme-font">
            {FONTS.map(f => <option key={f} value={f} style={{ fontFamily: f }}>{f}</option>)}
          </select>
          <p className="text-xs text-[#888] mt-1">{lang === 'fr' ? 'Polices Google Fonts. Aperçu disponible après enregistrement.' : 'Google Fonts. Preview available after saving.'}</p>
        </div>

        <div className="grid grid-cols-2 gap-4">
          {[
            { key: 'color_primary', label_fr: 'Couleur principale (rouge)', label_en: 'Primary color (red)' },
            { key: 'color_accent', label_fr: 'Couleur accent (jaune)', label_en: 'Accent color (yellow)' },
            { key: 'color_text', label_fr: 'Couleur texte', label_en: 'Text color' },
            { key: 'color_bg', label_fr: 'Couleur de fond', label_en: 'Background color' },
          ].map(c => (
            <div key={c.key}>
              <Label>{lang === 'fr' ? c.label_fr : c.label_en}</Label>
              <div className="mt-1 flex items-center gap-2">
                <input type="color" value={theme[c.key]} onChange={e => update(c.key, e.target.value)} className="w-12 h-9 rounded border border-[#e0e0e0] cursor-pointer" data-testid={`theme-${c.key}`} />
                <Input value={theme[c.key]} onChange={e => update(c.key, e.target.value)} className="flex-1" />
              </div>
            </div>
          ))}
        </div>

        <div className="border-t pt-5">
          <h3 className="font-heading text-lg font-semibold mb-3">{lang === 'fr' ? 'Coordonnées globales' : 'Global contact info'}</h3>
          <p className="text-xs text-[#888] mb-3">{lang === 'fr' ? 'Affichées dans le pied de page, la page Contact, et les notifications.' : 'Shown in the footer, Contact page, and notifications.'}</p>
          <div className="space-y-3">
            <div>
              <Label>{lang === 'fr' ? 'Adresse' : 'Address'}</Label>
              <Input value={theme.global_address || ''} onChange={e => update('global_address', e.target.value)} className="mt-1" data-testid="theme-address" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>{lang === 'fr' ? 'Téléphone' : 'Phone'}</Label>
                <Input value={theme.global_phone || ''} onChange={e => update('global_phone', e.target.value)} className="mt-1" data-testid="theme-phone" />
              </div>
              <div>
                <Label>Courriel</Label>
                <Input value={theme.global_email || ''} onChange={e => update('global_email', e.target.value)} className="mt-1" data-testid="theme-email" />
              </div>
            </div>
          </div>
        </div>

        {/* Preview */}
        <div className="border-t pt-5">
          <p className="text-xs text-[#888] mb-2">{lang === 'fr' ? 'Aperçu :' : 'Preview:'}</p>
          <div className="p-4 rounded-lg" style={{ backgroundColor: theme.color_bg, color: theme.color_text, fontFamily: `"${theme.font_family}", sans-serif` }}>
            <h4 className="text-xl font-bold mb-2">Resto Belgo</h4>
            <p className="text-sm mb-3">{theme.global_address}</p>
            <div className="flex gap-2">
              <button className="px-4 py-2 rounded text-white text-sm font-semibold" style={{ backgroundColor: theme.color_primary }}>Bouton primaire</button>
              <button className="px-4 py-2 rounded text-black text-sm font-semibold" style={{ backgroundColor: theme.color_accent }}>Bouton accent</button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// Sound alert helper
function playOrderSound() {
  try {
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    const playBeep = (freq, start, dur) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.frequency.value = freq;
      osc.type = 'sine';
      gain.gain.setValueAtTime(0.3, ctx.currentTime + start);
      gain.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + start + dur);
      osc.start(ctx.currentTime + start);
      osc.stop(ctx.currentTime + start + dur);
    };
    playBeep(880, 0, 0.2);
    playBeep(1100, 0.25, 0.2);
    playBeep(880, 0.5, 0.3);
  } catch (e) { if (process.env.NODE_ENV === 'development') console.log('Sound error', e); }
}

// Print receipt - uses DOM manipulation instead of document.write to avoid XSS
function printOrderReceipt(order) {
  const win = window.open('', '_blank', 'width=350,height=600');
  if (!win) return;
  const escHtml = (str) => {
    const div = document.createElement('div');
    div.textContent = str || '';
    return div.innerHTML;
  };
  const itemsHtml = (order.items || []).map(i =>
    `<tr><td>${escHtml(String(i.quantity))}x ${escHtml(i.name)}</td><td style="text-align:right">${escHtml(String((i.price * i.quantity).toFixed(2)))} $</td></tr>`
  ).join('');
  const content = `<!DOCTYPE html><html><head><title>Commande ${escHtml(order.order_number)}</title>
    <style>
      body{font-family:monospace;font-size:13px;padding:10px;max-width:300px;margin:0 auto}
      h1{font-size:18px;text-align:center;margin:5px 0}
      h2{font-size:14px;text-align:center;margin:2px 0;font-weight:normal}
      hr{border:1px dashed #000;margin:8px 0}
      table{width:100%;border-collapse:collapse}
      td{padding:3px 0}
      .total{font-size:16px;font-weight:bold}
      .center{text-align:center}
      @media print{body{margin:0;padding:5px}}
    </style></head><body>
    <h1>RESTO BELGO</h1>
    <h2>153 Champlain, Saint-Jean-sur-Richelieu</h2>
    <h2>Tel: (450) 347-8635</h2>
    <hr/>
    <p><strong>Commande: ${escHtml(order.order_number)}</strong></p>
    <p>Date: ${escHtml(new Date(order.created_at).toLocaleString('fr-CA'))}</p>
    <p>Client: ${escHtml(order.customer_name)}</p>
    <p>Tel: ${escHtml(order.customer_phone)}</p>
    <p>Cueillette: ${escHtml(order.pickup_time || 'N/A')}</p>
    ${order.notes ? `<p>Notes: ${escHtml(order.notes)}</p>` : ''}
    <hr/>
    <table>${itemsHtml}</table>
    <hr/>
    <table>
      <tr class="total"><td>TOTAL</td><td style="text-align:right">${escHtml(String(order.total?.toFixed(2)))} $</td></tr>
    </table>
    <hr/>
    <p class="center" style="font-size:11px">Merci de votre commande!</p>
    <p class="center" style="font-size:11px">Thank you for your order!</p>
    </body></html>`;
  win.document.open();
  const printDoc = win.document;
  printDoc.write(content);
  printDoc.close();
  setTimeout(() => { win.print(); }, 500);
}

// Print holiday receipt
function printHolidayReceipt(order) {
  const win = window.open('', '_blank', 'width=350,height=600');
  if (!win) return;
  const esc = (str) => { const d = document.createElement('div'); d.textContent = str || ''; return d.innerHTML; };
  const extrasHtml = (order.extras || []).map(e =>
    `<tr><td>${esc(String(e.quantity))}x ${esc(e.name)}</td><td style="text-align:right">${esc(String((e.price * e.quantity).toFixed(2)))} $</td></tr>`
  ).join('');
  const content = `<!DOCTYPE html><html><head><title>Commande ${esc(order.order_number)}</title>
    <style>body{font-family:monospace;font-size:13px;padding:10px;max-width:300px;margin:0 auto}h1{font-size:18px;text-align:center;margin:5px 0}h2{font-size:14px;text-align:center;margin:2px 0;font-weight:normal}hr{border:1px dashed #000;margin:8px 0}table{width:100%;border-collapse:collapse}td{padding:3px 0}.total{font-size:16px;font-weight:bold}.center{text-align:center}@media print{body{margin:0;padding:5px}}</style></head><body>
    <h1>RESTO BELGO - MENU DES FETES</h1><h2>153 Champlain, Saint-Jean-sur-Richelieu</h2><hr/>
    <p><strong>${esc(order.order_number)}</strong></p>
    <p>Date: ${esc(new Date(order.created_at).toLocaleString('fr-CA'))}</p>
    <p>Client: ${esc(order.customer_name)}</p><p>Tel: ${esc(order.customer_phone)}</p>
    <p>Ramassage: ${esc(order.pickup_date)}</p>
    ${order.notes ? `<p>Notes: ${esc(order.notes)}</p>` : ''}
    <hr/><table><tr><td><strong>${esc(order.package_name)} x${order.quantity}</strong></td><td style="text-align:right">${esc(String((order.package_price * order.quantity).toFixed(2)))} $</td></tr>${extrasHtml}</table><hr/>
    <table><tr class="total"><td>TOTAL</td><td style="text-align:right">${esc(String(order.total?.toFixed(2)))} $</td></tr></table><hr/>
    <p class="center" style="font-size:11px">Merci! Joyeuses fetes!</p></body></html>`;
  const printDoc = win.document;
  printDoc.open(); printDoc.write(content); printDoc.close();
  setTimeout(() => { win.print(); }, 500);
}

// Orders Management
function OrdersManagement() {
  const { lang } = useLanguage();
  const [orders, setOrders] = useState([]);
  const [lastCheck, setLastCheck] = useState('');
  const [soundEnabled, setSoundEnabled] = useState(true);
  const [autoPrint, setAutoPrint] = useState(true);
  const knownIdsRef = useRef(new Set());

  const fetchOrders = () => axiosAuth.get(`${API}/orders`).then(r => {
    const data = r.data;
    // On first load, just populate known IDs
    if (knownIdsRef.current.size === 0) {
      data.forEach(o => knownIdsRef.current.add(o.id));
    }
    setOrders(data);
  });

  // Poll for new orders every 10 seconds
  useEffect(() => {
    fetchOrders();
    const interval = setInterval(async () => {
      try {
        const r = await axiosAuth.get(`${API}/orders`);
        const data = r.data;
        const newOrders = data.filter(o => !knownIdsRef.current.has(o.id));
        if (newOrders.length > 0) {
          newOrders.forEach(o => knownIdsRef.current.add(o.id));
          if (soundEnabled) playOrderSound();
          if (autoPrint) {
            newOrders.forEach(o => printOrderReceipt(o));
          }
          toast.success(lang === 'fr'
            ? `${newOrders.length} nouvelle(s) commande(s)!`
            : `${newOrders.length} new order(s)!`);
        }
        setOrders(data);
      } catch (err) {
        if (process.env.NODE_ENV === 'development') console.error('Order poll error', err);
      }
    }, 10000);
    return () => clearInterval(interval);
  }, [soundEnabled, autoPrint, lang]); // eslint-disable-line react-hooks/exhaustive-deps

  const updateStatus = async (id, status) => {
    await axiosAuth.patch(`${API}/orders/${id}/status`, { status });
    fetchOrders();
    toast.success(lang === 'fr' ? 'Statut mis à jour' : 'Status updated');
  };

  const statusColors = {
    pending: '#f4d00d', confirmed: '#2E7D32', preparing: '#1976D2', ready: '#e53a3a', completed: '#555555', cancelled: '#D32F2F'
  };
  const statusLabels = {
    pending: { fr: 'En attente', en: 'Pending' }, confirmed: { fr: 'Confirmée', en: 'Confirmed' },
    preparing: { fr: 'En préparation', en: 'Preparing' }, ready: { fr: 'Prête', en: 'Ready' },
    completed: { fr: 'Complétée', en: 'Completed' }, cancelled: { fr: 'Annulée', en: 'Cancelled' }
  };

  return (
    <div data-testid="admin-orders-page">
      <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
        <h2 className="font-heading text-2xl font-bold text-[#1a1a1a]">{lang === 'fr' ? 'Commandes' : 'Orders'}</h2>
        <div className="flex items-center gap-4">
          <label className="flex items-center gap-2 text-sm cursor-pointer">
            <Switch checked={soundEnabled} onCheckedChange={setSoundEnabled} />
            <span className="flex items-center gap-1">{lang === 'fr' ? 'Son' : 'Sound'}</span>
          </label>
          <label className="flex items-center gap-2 text-sm cursor-pointer">
            <Switch checked={autoPrint} onCheckedChange={setAutoPrint} />
            <span>{lang === 'fr' ? 'Impression auto' : 'Auto-print'}</span>
          </label>
          <button onClick={() => playOrderSound()} className="px-3 py-1 text-xs rounded border border-[#e0e0e0] hover:bg-[#f5f5f0]" data-testid="test-sound">
            {lang === 'fr' ? 'Tester le son' : 'Test Sound'}
          </button>
        </div>
      </div>
      {orders.length === 0 ? (
        <p className="text-[#555555]">{lang === 'fr' ? 'Aucune commande' : 'No orders'}</p>
      ) : (
        <div className="space-y-4">
          {orders.map(order => (
            <div key={order.id} className={`bg-white rounded-xl border p-5 ${order.status === 'pending' ? 'border-[#f4d00d] border-2 shadow-md' : 'border-[#e0e0e0]'}`} data-testid={`order-${order.id}`}>
              <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
                <div>
                  <span className="font-bold text-[#e53a3a] text-lg">{order.order_number}</span>
                  <span className="text-sm text-[#555555] ml-3">{order.customer_name}</span>
                  {order.status === 'pending' && <Badge className="ml-2 animate-pulse bg-[#f4d00d] text-black text-xs">NEW</Badge>}
                </div>
                <div className="flex items-center gap-2">
                  <Badge style={{ backgroundColor: statusColors[order.status] || '#555555', color: order.status === 'pending' ? 'black' : 'white' }}>
                    {statusLabels[order.status]?.[lang] || order.status}
                  </Badge>
                  <button
                    onClick={() => printOrderReceipt(order)}
                    className="px-3 py-1 rounded text-xs font-medium bg-[#1a1a1a] text-white hover:bg-black"
                    data-testid={`print-order-${order.id}`}
                  >
                    {lang === 'fr' ? 'Imprimer' : 'Print'}
                  </button>
                </div>
              </div>
              <div className="text-sm text-[#555555] mb-3">
                <p>{order.customer_email} | {order.customer_phone}</p>
                <p>{lang === 'fr' ? 'Cueillette' : 'Pickup'}: {order.pickup_time} | Total: <strong>{order.total?.toFixed(2)} $</strong></p>
                {order.notes && <p className="italic mt-1">"{order.notes}"</p>}
              </div>
              <div className="mb-3">
                {order.items?.map((item, i) => (
                  <span key={i} className="text-xs bg-[#f5f5f0] px-2 py-1 rounded mr-2 mb-1 inline-block">
                    {item.quantity}x {item.name}
                  </span>
                ))}
              </div>
              <div className="flex flex-wrap gap-2">
                {['confirmed', 'preparing', 'ready', 'completed', 'cancelled'].map(s => (
                  <button
                    key={s}
                    onClick={() => updateStatus(order.id, s)}
                    disabled={order.status === s}
                    className={`px-3 py-1 rounded text-xs font-medium border transition-colors ${
                      order.status === s ? 'opacity-50 cursor-not-allowed' : 'hover:opacity-80'
                    }`}
                    style={{ borderColor: statusColors[s], color: statusColors[s] }}
                    data-testid={`order-status-${s}-${order.id}`}
                  >
                    {statusLabels[s]?.[lang] || s}
                  </button>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// Catering Management
function CateringManagement() {
  const { lang } = useLanguage();
  const [requests, setRequests] = useState([]);
  useEffect(() => { axiosAuth.get(`${API}/catering`).then(r => setRequests(r.data)); }, []);

  const updateStatus = async (id, status) => {
    await axiosAuth.patch(`${API}/catering/${id}/status`, { status });
    setRequests(prev => prev.map(r => r.id === id ? { ...r, status } : r));
    toast.success(lang === 'fr' ? 'Mis à jour' : 'Updated');
  };

  return (
    <div data-testid="admin-catering-page">
      <h2 className="font-heading text-2xl font-bold text-[#1a1a1a] mb-6">{lang === 'fr' ? 'Demandes traiteur' : 'Catering Requests'}</h2>
      {requests.length === 0 ? (
        <p className="text-[#555555]">{lang === 'fr' ? 'Aucune demande' : 'No requests'}</p>
      ) : (
        <div className="space-y-4">
          {requests.map(req => (
            <div key={req.id} className="bg-white rounded-xl border border-[#e0e0e0] p-5" data-testid={`catering-req-${req.id}`}>
              <div className="flex items-center justify-between mb-2">
                <h3 className="font-semibold text-[#1a1a1a]">{req.name}</h3>
                <Badge variant="outline">{req.status}</Badge>
              </div>
              <p className="text-sm text-[#555555]">{req.email} | {req.phone}</p>
              <p className="text-sm text-[#555555]">{lang === 'fr' ? 'Date' : 'Date'}: {req.event_date} | {req.guest_count} {lang === 'fr' ? 'invités' : 'guests'}</p>
              {req.message && <p className="text-sm text-[#555555] italic mt-1">"{req.message}"</p>}
              <div className="flex gap-2 mt-3">
                {['reviewed', 'confirmed', 'cancelled'].map(s => (
                  <button key={s} onClick={() => updateStatus(req.id, s)} className="px-3 py-1 rounded text-xs font-medium border border-[#e0e0e0] hover:bg-[#f5f5f0]">
                    {s}
                  </button>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// Food Truck Management
function FoodTruckManagement() {
  const { lang } = useLanguage();
  const [schedules, setSchedules] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState({ date: '', time_start: '', time_end: '', location_name_fr: '', location_name_en: '', address: '', lat: 0, lng: 0, menu_notes_fr: '', menu_notes_en: '', active: true });

  const fetch = () => {
    axiosAuth.get(`${API}/foodtruck/schedule/all`).then(r => setSchedules(r.data));
    axiosAuth.get(`${API}/foodtruck/bookings`).then(r => setBookings(r.data));
  };
  useEffect(() => { fetch(); }, []);

  const handleSave = async () => {
    await axiosAuth.post(`${API}/foodtruck/schedule`, form);
    setDialogOpen(false);
    fetch();
    toast.success('Saved!');
  };

  const handleDelete = async (id) => {
    await axiosAuth.delete(`${API}/foodtruck/schedule/${id}`);
    fetch();
  };

  return (
    <div data-testid="admin-foodtruck-page">
      <div className="flex items-center justify-between mb-6">
        <h2 className="font-heading text-2xl font-bold text-[#1a1a1a]">{lang === 'fr' ? 'Camion Belgo' : 'Food Truck'}</h2>
        <button onClick={() => setDialogOpen(true)} className="btn-belgo flex items-center gap-2 px-4 py-2 rounded-lg text-white text-sm font-semibold" style={{ backgroundColor: '#e53a3a' }} data-testid="add-schedule">
          <Plus className="w-4 h-4" /> {lang === 'fr' ? 'Ajouter horaire' : 'Add Schedule'}
        </button>
      </div>

      {/* Schedules */}
      <h3 className="font-semibold text-[#1a1a1a] mb-3">{lang === 'fr' ? 'Horaires' : 'Schedules'}</h3>
      <div className="space-y-3 mb-8">
        {schedules.map(s => (
          <div key={s.id} className="flex items-center gap-4 bg-white rounded-xl border border-[#e0e0e0] p-4" data-testid={`schedule-${s.id}`}>
            <div className="flex-1">
              <p className="font-semibold">{s.date} | {s.time_start}-{s.time_end}</p>
              <p className="text-sm text-[#555555]">{lang === 'fr' ? s.location_name_fr : s.location_name_en} - {s.address}</p>
            </div>
            <Badge variant={s.active ? 'default' : 'secondary'}>{s.active ? 'Active' : 'Inactive'}</Badge>
            <button onClick={() => handleDelete(s.id)} className="p-2 hover:bg-red-50 rounded text-red-600"><Trash2 className="w-4 h-4" /></button>
          </div>
        ))}
      </div>

      {/* Bookings */}
      <h3 className="font-semibold text-[#1a1a1a] mb-3">{lang === 'fr' ? 'Réservations' : 'Bookings'}</h3>
      <div className="space-y-3">
        {bookings.map(b => (
          <div key={b.id} className="bg-white rounded-xl border border-[#e0e0e0] p-4 text-sm" data-testid={`booking-${b.id}`}>
            <p className="font-semibold">{b.name} - {b.date}</p>
            <p className="text-[#555555]">{b.email} | {b.phone} | {b.guests} {lang === 'fr' ? 'invités' : 'guests'}</p>
            <p className="text-[#555555]">{b.location} | {lang === 'fr' ? 'Génératrice' : 'Generator'}: {b.generator}</p>
          </div>
        ))}
      </div>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>{lang === 'fr' ? 'Nouvel horaire' : 'New Schedule'}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-3 gap-3">
              <div><Label>Date</Label><Input type="date" value={form.date} onChange={e => setForm(f => ({ ...f, date: e.target.value }))} className="mt-1" /></div>
              <div><Label>{lang === 'fr' ? 'Début' : 'Start'}</Label><Input type="time" value={form.time_start} onChange={e => setForm(f => ({ ...f, time_start: e.target.value }))} className="mt-1" /></div>
              <div><Label>{lang === 'fr' ? 'Fin' : 'End'}</Label><Input type="time" value={form.time_end} onChange={e => setForm(f => ({ ...f, time_end: e.target.value }))} className="mt-1" /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Lieu (FR)</Label><Input value={form.location_name_fr} onChange={e => setForm(f => ({ ...f, location_name_fr: e.target.value }))} className="mt-1" /></div>
              <div><Label>Location (EN)</Label><Input value={form.location_name_en} onChange={e => setForm(f => ({ ...f, location_name_en: e.target.value }))} className="mt-1" /></div>
            </div>
            <div><Label>{lang === 'fr' ? 'Adresse' : 'Address'}</Label><Input value={form.address} onChange={e => setForm(f => ({ ...f, address: e.target.value }))} className="mt-1" /></div>
            <button onClick={handleSave} className="btn-belgo w-full px-4 py-2.5 rounded-lg text-white font-semibold" style={{ backgroundColor: '#e53a3a' }}>
              {lang === 'fr' ? 'Enregistrer' : 'Save'}
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// Messages Management
function MessagesManagement() {
  const { lang } = useLanguage();
  const [messages, setMessages] = useState([]);
  useEffect(() => { axiosAuth.get(`${API}/contact`).then(r => setMessages(r.data)); }, []);

  return (
    <div data-testid="admin-messages-page">
      <h2 className="font-heading text-2xl font-bold text-[#1a1a1a] mb-6">{lang === 'fr' ? 'Messages' : 'Messages'}</h2>
      {messages.length === 0 ? (
        <p className="text-[#555555]">{lang === 'fr' ? 'Aucun message' : 'No messages'}</p>
      ) : (
        <div className="space-y-3">
          {messages.map(msg => (
            <div key={msg.id} className="bg-white rounded-xl border border-[#e0e0e0] p-5" data-testid={`message-${msg.id}`}>
              <div className="flex items-center justify-between mb-2">
                <h3 className="font-semibold text-[#1a1a1a]">{msg.name}</h3>
                <span className="text-xs text-[#555555]">{new Date(msg.created_at).toLocaleDateString()}</span>
              </div>
              <p className="text-sm text-[#555555]">{msg.email} {msg.phone && `| ${msg.phone}`}</p>
              {msg.subject && <p className="text-sm font-medium text-[#1a1a1a] mt-1">{msg.subject}</p>}
              <p className="text-sm text-[#555555] mt-1">{msg.message}</p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// Users Management
function UsersManagement() {
  const { lang } = useLanguage();
  const { user: currentUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState({ name: '', email: '', password: '' });
  const [pwDialog, setPwDialog] = useState({ open: false, userId: '', password: '' });

  const fetchUsers = useCallback(() => axiosAuth.get(`${API}/admin/users`).then(r => setUsers(r.data)), []);
  useEffect(() => { fetchUsers(); }, [fetchUsers]);

  const handleCreate = async () => {
    try {
      await axiosAuth.post(`${API}/admin/users`, form);
      toast.success(lang === 'fr' ? 'Administrateur ajouté!' : 'Admin added!');
      setDialogOpen(false);
      setForm({ name: '', email: '', password: '' });
      fetchUsers();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error');
    }
  };

  const handleDelete = async (id, email, role) => {
    if (role === 'superadmin') {
      toast.error(lang === 'fr' ? 'Impossible de supprimer le super administrateur' : 'Cannot delete super admin');
      return;
    }
    if (!window.confirm(lang === 'fr' ? `Supprimer ${email}?` : `Delete ${email}?`)) return;
    try {
      await axiosAuth.delete(`${API}/admin/users/${id}`);
      toast.success(lang === 'fr' ? 'Supprimé' : 'Deleted');
      fetchUsers();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error');
    }
  };

  const handlePasswordChange = async () => {
    try {
      await axiosAuth.put(`${API}/admin/users/${pwDialog.userId}/password`, { password: pwDialog.password });
      toast.success(lang === 'fr' ? 'Mot de passe mis à jour!' : 'Password updated!');
      setPwDialog({ open: false, userId: '', password: '' });
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error');
    }
  };

  return (
    <div data-testid="admin-users-page">
      <div className="flex items-center justify-between mb-6">
        <h2 className="font-heading text-2xl font-bold text-[#1a1a1a]">{lang === 'fr' ? 'Gestion des administrateurs' : 'Admin Management'}</h2>
        <button onClick={() => setDialogOpen(true)} className="btn-belgo flex items-center gap-2 px-4 py-2 rounded-lg text-white text-sm font-semibold bg-[#e53a3a] hover:bg-[#c62828]" data-testid="add-admin-user">
          <Plus className="w-4 h-4" /> {lang === 'fr' ? 'Ajouter un admin' : 'Add Admin'}
        </button>
      </div>

      <div className="space-y-3">
        {users.map(u => (
          <div key={u.id} className={`flex items-center gap-4 bg-white rounded-xl border p-5 ${u.is_superadmin ? 'border-[#f4d00d] border-2' : 'border-[#e0e0e0]'}`} data-testid={`user-${u.id}`}>
            <div className="w-10 h-10 rounded-full flex items-center justify-center text-white font-bold text-sm shrink-0" style={{ backgroundColor: u.is_superadmin ? '#f4d00d' : '#e53a3a', color: u.is_superadmin ? '#1a1a1a' : 'white' }}>
              {u.name ? u.name[0].toUpperCase() : u.email[0].toUpperCase()}
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <h4 className="font-semibold text-[#1a1a1a] truncate">{u.name || u.email}</h4>
                {u.is_superadmin && (
                  <Badge className="text-xs bg-[#f4d00d] text-black border-0">Super Admin</Badge>
                )}
                {!u.is_superadmin && (
                  <Badge variant="outline" className="text-xs">Admin</Badge>
                )}
              </div>
              <p className="text-sm text-[#555555]">{u.email}</p>
            </div>
            <button
              onClick={() => setPwDialog({ open: true, userId: u.id, password: '' })}
              className="px-3 py-1.5 rounded-lg text-xs font-medium border border-[#e0e0e0] hover:bg-[#f5f5f0]"
              data-testid={`change-pw-${u.id}`}
            >
              {lang === 'fr' ? 'Mot de passe' : 'Password'}
            </button>
            {!u.is_superadmin && (
              <button
                onClick={() => handleDelete(u.id, u.email, u.role)}
                className="p-2 hover:bg-red-50 rounded-lg text-red-600"
                data-testid={`delete-user-${u.id}`}
              >
                <Trash2 className="w-4 h-4" />
              </button>
            )}
            {u.is_superadmin && (
              <div className="p-2 text-[#f4d00d]" title={lang === 'fr' ? 'Irrevocable' : 'Irrevocable'}>
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Add Admin Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading">{lang === 'fr' ? 'Ajouter un administrateur' : 'Add Administrator'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div><Label>{lang === 'fr' ? 'Nom' : 'Name'}</Label><Input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} className="mt-1" data-testid="new-admin-name" /></div>
            <div><Label>{lang === 'fr' ? 'Courriel' : 'Email'}</Label><Input type="email" value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} required className="mt-1" data-testid="new-admin-email" /></div>
            <div><Label>{lang === 'fr' ? 'Mot de passe' : 'Password'}</Label><Input type="password" value={form.password} onChange={e => setForm(f => ({ ...f, password: e.target.value }))} required className="mt-1" data-testid="new-admin-password" /></div>
            <button onClick={handleCreate} className="btn-belgo w-full px-4 py-2.5 rounded-lg text-white font-semibold bg-[#e53a3a] hover:bg-[#c62828]" data-testid="create-admin-btn">
              {lang === 'fr' ? 'Créer l\'administrateur' : 'Create Administrator'}
            </button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Change Password Dialog */}
      <Dialog open={pwDialog.open} onOpenChange={v => setPwDialog(p => ({ ...p, open: v }))}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading">{lang === 'fr' ? 'Changer le mot de passe' : 'Change Password'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div><Label>{lang === 'fr' ? 'Nouveau mot de passe' : 'New Password'}</Label><Input type="password" value={pwDialog.password} onChange={e => setPwDialog(p => ({ ...p, password: e.target.value }))} className="mt-1" data-testid="new-password-input" /></div>
            <button onClick={handlePasswordChange} className="btn-belgo w-full px-4 py-2.5 rounded-lg text-white font-semibold bg-[#e53a3a] hover:bg-[#c62828]" data-testid="save-password-btn">
              {lang === 'fr' ? 'Enregistrer' : 'Save'}
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// Site Settings Management
function SiteSettings() {
  const { lang } = useLanguage();
  const [monthly, setMonthly] = useState({ active: true, name_fr: '', name_en: '', description_fr: '', description_en: '', price: '', image_url: '', month_fr: '', month_en: '' });
  const [launch, setLaunch] = useState({ launch_date: '', site_live: false, access_code: '' });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    axiosAuth.get(`${API}/settings/monthly-special`).then(r => {
      if (r.data && r.data.name_fr) setMonthly({ ...r.data, price: String(r.data.price || '') });
    });
    axiosAuth.get(`${API}/settings/launch/admin`).then(r => {
      if (r.data) setLaunch(r.data);
    });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const saveMonthly = async () => {
    setSaving(true);
    await axiosAuth.put(`${API}/settings/monthly-special`, { ...monthly, price: parseFloat(monthly.price) || 0 });
    toast.success(lang === 'fr' ? 'Poutine du mois mise à jour!' : 'Monthly special updated!');
    setSaving(false);
  };

  const saveLaunch = async () => {
    setSaving(true);
    await axiosAuth.put(`${API}/settings/launch`, launch);
    toast.success(lang === 'fr' ? 'Paramètres de lancement mis à jour!' : 'Launch settings updated!');
    setSaving(false);
  };

  return (
    <div data-testid="admin-settings-page" className="space-y-8">
      <h2 className="font-heading text-2xl font-bold text-[#1a1a1a]">{lang === 'fr' ? 'Paramètres du site' : 'Site Settings'}</h2>

      {/* Monthly Special */}
      <div className="bg-white rounded-xl border border-[#e0e0e0] p-6">
        <h3 className="font-heading text-xl font-semibold text-[#1a1a1a] mb-4 flex items-center gap-2">
          <span className="text-[#f4d00d]">&#9733;</span> {lang === 'fr' ? 'Poutine du mois (Popup)' : 'Monthly Poutine (Popup)'}
        </h3>
        <div className="space-y-4">
          <label className="flex items-center gap-2 text-sm">
            <Switch checked={monthly.active} onCheckedChange={v => setMonthly(m => ({ ...m, active: v }))} />
            {lang === 'fr' ? 'Popup actif' : 'Popup active'}
          </label>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div><Label>Nom (FR)</Label><Input value={monthly.name_fr} onChange={e => setMonthly(m => ({ ...m, name_fr: e.target.value }))} className="mt-1" data-testid="monthly-name-fr" /></div>
            <div><Label>Name (EN)</Label><Input value={monthly.name_en} onChange={e => setMonthly(m => ({ ...m, name_en: e.target.value }))} className="mt-1" /></div>
            <div><Label>Description (FR)</Label><Textarea value={monthly.description_fr} onChange={e => setMonthly(m => ({ ...m, description_fr: e.target.value }))} rows={2} className="mt-1" /></div>
            <div><Label>Description (EN)</Label><Textarea value={monthly.description_en} onChange={e => setMonthly(m => ({ ...m, description_en: e.target.value }))} rows={2} className="mt-1" /></div>
            <div><Label>{lang === 'fr' ? 'Prix ($)' : 'Price ($)'}</Label><Input type="number" step="0.01" value={monthly.price} onChange={e => setMonthly(m => ({ ...m, price: e.target.value }))} className="mt-1" /></div>
            <div className="col-span-2">
              <Label>{lang === 'fr' ? 'Image (upload ou URL)' : 'Image (upload or URL)'}</Label>
              <Input value={monthly.image_url} onChange={e => setMonthly(m => ({ ...m, image_url: e.target.value }))} placeholder="https://..." className="mt-1" data-testid="monthly-image-url" />
              <input
                type="file"
                accept="image/*"
                onChange={async (e) => {
                  const file = e.target.files?.[0];
                  if (!file) return;
                  try {
                    const fd = new FormData();
                    fd.append('file', file);
                    const { data } = await axiosAuth.post(`${API}/upload`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
                    const fullUrl = `${process.env.REACT_APP_BACKEND_URL}${data.url}`;
                    setMonthly(m => ({ ...m, image_url: fullUrl }));
                    toast.success(lang === 'fr' ? 'Image uploadée!' : 'Image uploaded!');
                  } catch (err) {
                    toast.error(err?.response?.data?.detail || 'Upload error');
                  }
                }}
                className="mt-2 text-xs"
                data-testid="monthly-image-file"
              />
            </div>
            <div><Label>{lang === 'fr' ? 'Mois (FR)' : 'Month (FR)'}</Label><Input value={monthly.month_fr} onChange={e => setMonthly(m => ({ ...m, month_fr: e.target.value }))} placeholder="Ex: Mai 2026" className="mt-1" /></div>
            <div><Label>{lang === 'fr' ? 'Mois (EN)' : 'Month (EN)'}</Label><Input value={monthly.month_en} onChange={e => setMonthly(m => ({ ...m, month_en: e.target.value }))} placeholder="Ex: May 2026" className="mt-1" /></div>
          </div>
          {monthly.image_url && <img src={monthly.image_url} alt="Preview" className="h-32 rounded-lg object-cover" />}
          <button onClick={saveMonthly} disabled={saving} className="btn-belgo px-6 py-2 rounded-lg text-white font-semibold bg-[#e53a3a] hover:bg-[#c62828]" data-testid="save-monthly">
            {saving ? '...' : (lang === 'fr' ? 'Enregistrer' : 'Save')}
          </button>
        </div>
      </div>

      {/* Launch Settings */}
      <div className="bg-white rounded-xl border border-[#e0e0e0] p-6">
        <h3 className="font-heading text-xl font-semibold text-[#1a1a1a] mb-4 flex items-center gap-2">
          <span className="text-[#e53a3a]">&#128640;</span> {lang === 'fr' ? 'Dévoilement du site' : 'Site Launch'}
        </h3>
        <div className="space-y-4">
          <label className="flex items-center gap-2 text-sm font-semibold">
            <Switch checked={launch.site_live} onCheckedChange={v => setLaunch(l => ({ ...l, site_live: v }))} />
            <span className={launch.site_live ? 'text-green-600' : 'text-[#e53a3a]'}>
              {launch.site_live
                ? (lang === 'fr' ? 'Site EN LIGNE' : 'Site is LIVE')
                : (lang === 'fr' ? 'Site en mode "Coming Soon"' : 'Site in "Coming Soon" mode')}
            </span>
          </label>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <Label>{lang === 'fr' ? 'Date de lancement' : 'Launch Date'}</Label>
              <Input type="datetime-local" value={launch.launch_date?.replace('T', 'T').slice(0, 16)} onChange={e => setLaunch(l => ({ ...l, launch_date: e.target.value }))} className="mt-1" data-testid="launch-date" />
            </div>
            <div>
              <Label>{lang === 'fr' ? 'Code d\'accès secret' : 'Secret Access Code'}</Label>
              <Input value={launch.access_code} onChange={e => setLaunch(l => ({ ...l, access_code: e.target.value }))} className="mt-1" data-testid="access-code-admin" />
              <p className="text-xs text-[#555555] mt-1">{lang === 'fr' ? 'Ce code permet d\'accéder au site avant le lancement' : 'This code allows access before launch'}</p>
            </div>
          </div>
          <button onClick={saveLaunch} disabled={saving} className="btn-belgo px-6 py-2 rounded-lg text-white font-semibold bg-[#e53a3a] hover:bg-[#c62828]" data-testid="save-launch">
            {saving ? '...' : (lang === 'fr' ? 'Enregistrer' : 'Save')}
          </button>
        </div>
      </div>
    </div>
  );
}

// Notifications Config Admin
function NotificationsAdmin() {
  const { lang } = useLanguage();
  const [config, setConfig] = useState({
    restaurant_email: '', contact_email: '', restaurant_phone: '',
    email_to_customer: true, sms_to_customer: true, sms_to_customer_on_order: true,
    smtp: { enabled: false, host: '', port: '587', username: '', password: '', from_email: '', use_tls: true },
    voipms: { enabled: false, api_username: '', api_password: '', did: '' },
    customer_templates: {
      customer_email_subject_fr: 'Confirmation commande {order_number} - Resto Belgo',
      customer_email_subject_en: 'Order confirmation {order_number} - Resto Belgo',
      customer_email_body_fr: '<h2>Merci pour votre commande!</h2><p>Bonjour {name},</p><p>Votre commande <strong>{order_number}</strong> a bien été reçue.</p><p><strong>Ramassage:</strong> {pickup_time}</p><p><strong>Total: {total}$</strong></p><p>Resto Belgo - 153 Champlain, Saint-Jean-sur-Richelieu<br>(450) 347-8635</p>',
      customer_email_body_en: '<h2>Thank you for your order!</h2><p>Hello {name},</p><p>Your order <strong>{order_number}</strong> has been received.</p><p><strong>Pickup:</strong> {pickup_time}</p><p><strong>Total: {total}$</strong></p><p>Resto Belgo - 153 Champlain, Saint-Jean-sur-Richelieu<br>(450) 347-8635</p>',
      customer_sms_body_fr: 'Resto Belgo: Bonjour {name}, votre commande {order_number} (total {total}$) a ete recue. Ramassage: {pickup_time}. Merci!',
      customer_sms_body_en: 'Resto Belgo: Hello {name}, your order {order_number} (total {total}$) has been received. Pickup: {pickup_time}. Thanks!',
    },
  });
  const [saving, setSaving] = useState(false);
  const [testPhone, setTestPhone] = useState('');
  const [testEmail, setTestEmail] = useState('');

  useEffect(() => {
    axiosAuth.get(`${API}/settings/notifications`).then(r => {
      if (r.data && Object.keys(r.data).length > 0) setConfig(prev => ({ ...prev, ...r.data }));
    });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const save = async () => {
    setSaving(true);
    await axiosAuth.put(`${API}/settings/notifications`, config);
    toast.success(lang === 'fr' ? 'Configuration sauvegardée!' : 'Config saved!');
    setSaving(false);
  };

  const sendTestSms = async () => {
    if (!testPhone) return;
    const r = await axiosAuth.post(`${API}/settings/notifications/test-sms`, { destination: testPhone });
    toast[r.data.success ? 'success' : 'error'](r.data.success ? 'SMS envoyé!' : 'Échec SMS');
  };

  const sendTestEmail = async () => {
    if (!testEmail) return;
    const r = await axiosAuth.post(`${API}/settings/notifications/test-email`, { destination: testEmail });
    toast[r.data.success ? 'success' : 'error'](r.data.success ? 'Courriel envoyé!' : 'Échec courriel');
  };

  const updateSmtp = (key, val) => setConfig(c => ({ ...c, smtp: { ...c.smtp, [key]: val } }));
  const updateVoip = (key, val) => setConfig(c => ({ ...c, voipms: { ...c.voipms, [key]: val } }));
  const updateTpl = (key, val) => setConfig(c => ({ ...c, customer_templates: { ...(c.customer_templates || {}), [key]: val } }));

  return (
    <div data-testid="admin-notifications-page" className="space-y-6">
      <h2 className="font-heading text-2xl font-bold text-[#1a1a1a]">{lang === 'fr' ? 'Notifications' : 'Notifications'}</h2>

      {/* General */}
      <div className="bg-white rounded-xl border border-[#e0e0e0] p-5 space-y-4">
        <h3 className="font-semibold">{lang === 'fr' ? 'Général' : 'General'}</h3>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div>
            <Label>{lang === 'fr' ? 'Courriel du restaurant (reçoit les commandes)' : 'Restaurant email (receives orders)'}</Label>
            <Input value={config.restaurant_email} onChange={e => setConfig(c => ({ ...c, restaurant_email: e.target.value }))} className="mt-1" data-testid="notif-restaurant-email" />
          </div>
          <div>
            <Label>{lang === 'fr' ? 'Courriel de contact (reçoit les messages)' : 'Contact email (receives messages)'}</Label>
            <Input value={config.contact_email || ''} onChange={e => setConfig(c => ({ ...c, contact_email: e.target.value }))} placeholder={config.restaurant_email || 'info@restobelgo.com'} className="mt-1" data-testid="notif-contact-email" />
            <p className="text-xs text-[#888] mt-1">{lang === 'fr' ? 'Si vide, utilise le courriel du restaurant puis le courriel global.' : 'If empty, uses the restaurant email, then the global email.'}</p>
          </div>
          <div>
            <Label>{lang === 'fr' ? 'Téléphone du restaurant (reçoit les SMS)' : 'Restaurant phone (receives SMS)'}</Label>
            <Input value={config.restaurant_phone} onChange={e => setConfig(c => ({ ...c, restaurant_phone: e.target.value }))} placeholder="4501234567" className="mt-1" data-testid="notif-restaurant-phone" />
          </div>
        </div>
        <div className="flex gap-6 flex-wrap">
          <label className="flex items-center gap-2 text-sm">
            <Switch checked={config.email_to_customer} onCheckedChange={v => setConfig(c => ({ ...c, email_to_customer: v }))} />
            {lang === 'fr' ? 'Envoyer courriel au client (à la commande)' : 'Email customer (on order)'}
          </label>
          <label className="flex items-center gap-2 text-sm">
            <Switch checked={config.sms_to_customer_on_order} onCheckedChange={v => setConfig(c => ({ ...c, sms_to_customer_on_order: v }))} />
            {lang === 'fr' ? 'Envoyer SMS au client (à la commande)' : 'SMS customer (on order)'}
          </label>
          <label className="flex items-center gap-2 text-sm">
            <Switch checked={config.sms_to_customer} onCheckedChange={v => setConfig(c => ({ ...c, sms_to_customer: v }))} />
            {lang === 'fr' ? 'Envoyer SMS au client (quand prête)' : 'SMS customer (when ready)'}
          </label>
        </div>
      </div>

      {/* SMTP Config */}
      <div className="bg-white rounded-xl border border-[#e0e0e0] p-5 space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="font-semibold">{lang === 'fr' ? 'Configuration SMTP (Courriel)' : 'SMTP Config (Email)'}</h3>
          <label className="flex items-center gap-2 text-sm">
            <Switch checked={config.smtp.enabled} onCheckedChange={v => updateSmtp('enabled', v)} />
            {config.smtp.enabled ? 'Actif' : 'Inactif'}
          </label>
        </div>
        {config.smtp.enabled && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            <div><Label>SMTP Host</Label><Input value={config.smtp.host} onChange={e => updateSmtp('host', e.target.value)} placeholder="smtp.gmail.com" className="mt-1" /></div>
            <div><Label>Port</Label><Input value={config.smtp.port} onChange={e => updateSmtp('port', e.target.value)} placeholder="587" className="mt-1" /></div>
            <div><Label>{lang === 'fr' ? 'Nom d\'utilisateur' : 'Username'}</Label><Input value={config.smtp.username} onChange={e => updateSmtp('username', e.target.value)} className="mt-1" /></div>
            <div><Label>{lang === 'fr' ? 'Mot de passe' : 'Password'}</Label><Input type="password" value={config.smtp.password} onChange={e => updateSmtp('password', e.target.value)} className="mt-1" /></div>
            <div><Label>{lang === 'fr' ? 'Courriel expéditeur' : 'From email'}</Label><Input value={config.smtp.from_email} onChange={e => updateSmtp('from_email', e.target.value)} placeholder="noreply@restobelgo.com" className="mt-1" /></div>
            <div className="flex items-end">
              <label className="flex items-center gap-2 text-sm pb-2"><Switch checked={config.smtp.use_tls} onCheckedChange={v => updateSmtp('use_tls', v)} /> TLS</label>
            </div>
          </div>
        )}
        {config.smtp.enabled && (
          <div className="flex items-end gap-3 pt-2 border-t border-[#e0e0e0]">
            <div className="flex-1"><Label>{lang === 'fr' ? 'Tester le courriel' : 'Test email'}</Label><Input value={testEmail} onChange={e => setTestEmail(e.target.value)} placeholder="test@example.com" className="mt-1" /></div>
            <button onClick={sendTestEmail} className="px-4 py-2 rounded-lg text-sm font-semibold bg-[#1a1a1a] text-white hover:bg-black">{lang === 'fr' ? 'Envoyer test' : 'Send test'}</button>
          </div>
        )}
      </div>

      {/* VoIP.ms SMS */}
      <div className="bg-white rounded-xl border border-[#e0e0e0] p-5 space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="font-semibold">VoIP.ms SMS</h3>
          <label className="flex items-center gap-2 text-sm">
            <Switch checked={config.voipms.enabled} onCheckedChange={v => updateVoip('enabled', v)} />
            {config.voipms.enabled ? 'Actif' : 'Inactif'}
          </label>
        </div>
        {config.voipms.enabled && (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div><Label>API Username ({lang === 'fr' ? 'courriel VoIP.ms' : 'VoIP.ms email'})</Label><Input value={config.voipms.api_username} onChange={e => updateVoip('api_username', e.target.value)} placeholder="user@example.com" className="mt-1" /></div>
              <div><Label>API Password</Label><Input type="password" value={config.voipms.api_password} onChange={e => updateVoip('api_password', e.target.value)} className="mt-1" /></div>
              <div><Label>DID ({lang === 'fr' ? 'numéro source SMS' : 'SMS source number'})</Label><Input value={config.voipms.did} onChange={e => updateVoip('did', e.target.value)} placeholder="4501234567" className="mt-1" /></div>
            </div>
            <p className="text-xs text-[#555555]">{lang === 'fr' ? 'Note: Activez l\'API dans votre portail VoIP.ms (Menu > SOAP/REST API). Ajoutez l\'IP du serveur à la liste blanche. Activez le SMS sur votre DID.' : 'Note: Enable API in your VoIP.ms portal (Menu > SOAP/REST API). Whitelist server IP. Enable SMS on your DID.'}</p>
            <div className="flex items-end gap-3 pt-2 border-t border-[#e0e0e0]">
              <div className="flex-1"><Label>{lang === 'fr' ? 'Tester le SMS' : 'Test SMS'}</Label><Input value={testPhone} onChange={e => setTestPhone(e.target.value)} placeholder="4501234567" className="mt-1" /></div>
              <button onClick={sendTestSms} className="px-4 py-2 rounded-lg text-sm font-semibold bg-[#1a1a1a] text-white hover:bg-black">{lang === 'fr' ? 'Envoyer test' : 'Send test'}</button>
            </div>
          </>
        )}
      </div>

      {/* Customer Templates (Email + SMS, FR + EN) */}
      <div className="bg-white rounded-xl border border-[#e0e0e0] p-5 space-y-4" data-testid="customer-templates-section">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <h3 className="font-semibold">{lang === 'fr' ? 'Modèles client (Courriel + SMS)' : 'Customer templates (Email + SMS)'}</h3>
          <p className="text-xs text-[#555555]">
            {lang === 'fr' ? 'Variables disponibles :' : 'Available variables:'}{' '}
            <code className="bg-[#f5f5f0] px-1.5 py-0.5 rounded text-[#e53a3a]">{'{name}'}</code>{' '}
            <code className="bg-[#f5f5f0] px-1.5 py-0.5 rounded text-[#e53a3a]">{'{order_number}'}</code>{' '}
            <code className="bg-[#f5f5f0] px-1.5 py-0.5 rounded text-[#e53a3a]">{'{total}'}</code>{' '}
            <code className="bg-[#f5f5f0] px-1.5 py-0.5 rounded text-[#e53a3a]">{'{pickup_time}'}</code>
          </p>
        </div>

        {/* FR templates */}
        <div className="border-l-4 border-[#e53a3a] pl-4 space-y-3">
          <p className="text-xs font-bold uppercase tracking-wider text-[#e53a3a]">Français</p>
          <div>
            <Label>{lang === 'fr' ? 'Sujet courriel (FR)' : 'Email subject (FR)'}</Label>
            <Input value={config.customer_templates?.customer_email_subject_fr || ''} onChange={e => updateTpl('customer_email_subject_fr', e.target.value)} className="mt-1" data-testid="tpl-email-subject-fr" />
          </div>
          <div>
            <Label>{lang === 'fr' ? 'Corps courriel HTML (FR)' : 'Email body HTML (FR)'}</Label>
            <Textarea rows={5} value={config.customer_templates?.customer_email_body_fr || ''} onChange={e => updateTpl('customer_email_body_fr', e.target.value)} className="mt-1 font-mono text-xs" data-testid="tpl-email-body-fr" />
          </div>
          <div>
            <Label>{lang === 'fr' ? 'Message SMS (FR) — max 160 caractères' : 'SMS message (FR) — max 160 chars'}</Label>
            <Textarea rows={2} maxLength={160} value={config.customer_templates?.customer_sms_body_fr || ''} onChange={e => updateTpl('customer_sms_body_fr', e.target.value)} className="mt-1" data-testid="tpl-sms-body-fr" />
            <p className="text-xs text-[#888] mt-1">{(config.customer_templates?.customer_sms_body_fr || '').length}/160</p>
          </div>
        </div>

        {/* EN templates */}
        <div className="border-l-4 border-[#1a1a1a] pl-4 space-y-3">
          <p className="text-xs font-bold uppercase tracking-wider text-[#1a1a1a]">English</p>
          <div>
            <Label>{lang === 'fr' ? 'Sujet courriel (EN)' : 'Email subject (EN)'}</Label>
            <Input value={config.customer_templates?.customer_email_subject_en || ''} onChange={e => updateTpl('customer_email_subject_en', e.target.value)} className="mt-1" data-testid="tpl-email-subject-en" />
          </div>
          <div>
            <Label>{lang === 'fr' ? 'Corps courriel HTML (EN)' : 'Email body HTML (EN)'}</Label>
            <Textarea rows={5} value={config.customer_templates?.customer_email_body_en || ''} onChange={e => updateTpl('customer_email_body_en', e.target.value)} className="mt-1 font-mono text-xs" data-testid="tpl-email-body-en" />
          </div>
          <div>
            <Label>{lang === 'fr' ? 'Message SMS (EN) — max 160 caractères' : 'SMS message (EN) — max 160 chars'}</Label>
            <Textarea rows={2} maxLength={160} value={config.customer_templates?.customer_sms_body_en || ''} onChange={e => updateTpl('customer_sms_body_en', e.target.value)} className="mt-1" data-testid="tpl-sms-body-en" />
            <p className="text-xs text-[#888] mt-1">{(config.customer_templates?.customer_sms_body_en || '').length}/160</p>
          </div>
        </div>
      </div>

      {/* Info box */}
      <div className="bg-[#f4d00d]/10 border border-[#f4d00d]/30 rounded-xl p-4 text-sm text-[#1a1a1a]">
        <strong>{lang === 'fr' ? 'Quand les notifications sont-elles envoyées?' : 'When are notifications sent?'}</strong>
        <ul className="mt-2 space-y-1 text-[#555555]">
          <li>{lang === 'fr' ? '- Courriel au restaurant: à chaque nouvelle commande' : '- Email to restaurant: on every new order'}</li>
          <li>{lang === 'fr' ? '- Courriel & SMS au client: à la création de la commande (utilise les modèles ci-dessus)' : '- Email & SMS to customer: on order creation (uses templates above)'}</li>
          <li>{lang === 'fr' ? '- SMS au client (prête): quand la commande passe au statut "Prête"' : '- SMS to customer (ready): when order status changes to "Ready"'}</li>
        </ul>
      </div>

      <button onClick={save} disabled={saving} className="btn-belgo px-6 py-2.5 rounded-lg text-white font-semibold bg-[#e53a3a] hover:bg-[#c62828]" data-testid="save-notifications">
        {saving ? '...' : (lang === 'fr' ? 'Enregistrer la configuration' : 'Save Configuration')}
      </button>
    </div>
  );
}

// Holiday Menu Admin
function HolidayAdmin() {
  const { lang } = useLanguage();
  const [config, setConfig] = useState(null);
  const [orders, setOrders] = useState([]);
  const [saving, setSaving] = useState(false);
  const [tab, setTab] = useState('config');

  useEffect(() => {
    axiosAuth.get(`${API}/holiday-menu/admin`).then(r => setConfig(r.data));
    axiosAuth.get(`${API}/holiday-menu/orders`).then(r => setOrders(r.data));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const saveConfig = async () => {
    setSaving(true);
    await axiosAuth.put(`${API}/holiday-menu/admin`, config);
    toast.success(lang === 'fr' ? 'Sauvegardé!' : 'Saved!');
    setSaving(false);
  };

  const updateOrder = async (id, status) => {
    await axiosAuth.patch(`${API}/holiday-menu/orders/${id}/status`, { status });
    setOrders(prev => prev.map(o => o.id === id ? { ...o, status } : o));
    toast.success(lang === 'fr' ? 'Mis à jour' : 'Updated');
  };

  const addExtra = () => {
    setConfig(c => ({ ...c, extras: [...(c.extras || []), { id: `extra_${Date.now()}`, name_fr: '', name_en: '', price: 0 }] }));
  };

  const removeExtra = (idx) => {
    setConfig(c => ({ ...c, extras: c.extras.filter((_, i) => i !== idx) }));
  };

  const addPickupDate = () => {
    const date = prompt(lang === 'fr' ? 'Date (AAAA-MM-JJ):' : 'Date (YYYY-MM-DD):');
    if (date && /^\d{4}-\d{2}-\d{2}$/.test(date)) {
      setConfig(c => ({ ...c, pickup_dates: [...(c.pickup_dates || []), date] }));
    }
  };

  const removePickupDate = (idx) => {
    setConfig(c => ({ ...c, pickup_dates: c.pickup_dates.filter((_, i) => i !== idx) }));
  };

  if (!config) return <p>Loading...</p>;

  return (
    <div data-testid="admin-holiday-page">
      <div className="flex items-center justify-between mb-6">
        <h2 className="font-heading text-2xl font-bold text-[#1a1a1a]">{lang === 'fr' ? 'Menu des Fêtes' : 'Holiday Menu'}</h2>
        <label className="flex items-center gap-2 text-sm font-semibold">
          <Switch checked={config.active} onCheckedChange={v => setConfig(c => ({ ...c, active: v }))} />
          <span className={config.active ? 'text-green-600' : 'text-[#e53a3a]'}>{config.active ? (lang === 'fr' ? 'ACTIF' : 'ACTIVE') : (lang === 'fr' ? 'INACTIF' : 'INACTIVE')}</span>
        </label>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 mb-6">
        {['config', 'orders'].map(t => (
          <button key={t} onClick={() => setTab(t)} className={`px-4 py-2 rounded-lg text-sm font-medium ${tab === t ? 'bg-[#e53a3a] text-white' : 'bg-white border border-[#e0e0e0] text-[#1a1a1a]'}`}>
            {t === 'config' ? (lang === 'fr' ? 'Configuration' : 'Config') : (lang === 'fr' ? `Commandes (${orders.length})` : `Orders (${orders.length})`)}
          </button>
        ))}
      </div>

      {tab === 'config' && (
        <div className="space-y-6">
          {/* Basic Info */}
          <div className="bg-white rounded-xl border border-[#e0e0e0] p-5 space-y-4">
            <h3 className="font-semibold">{lang === 'fr' ? 'Informations générales' : 'General Info'}</h3>
            <div className="grid grid-cols-2 gap-4">
              <div><Label>Titre (FR)</Label><Input value={config.title_fr || ''} onChange={e => setConfig(c => ({ ...c, title_fr: e.target.value }))} className="mt-1" /></div>
              <div><Label>Title (EN)</Label><Input value={config.title_en || ''} onChange={e => setConfig(c => ({ ...c, title_en: e.target.value }))} className="mt-1" /></div>
              <div><Label>Sous-titre (FR)</Label><Input value={config.subtitle_fr || ''} onChange={e => setConfig(c => ({ ...c, subtitle_fr: e.target.value }))} className="mt-1" /></div>
              <div><Label>Subtitle (EN)</Label><Input value={config.subtitle_en || ''} onChange={e => setConfig(c => ({ ...c, subtitle_en: e.target.value }))} className="mt-1" /></div>
            </div>
          </div>

          {/* Packages */}
          <div className="bg-white rounded-xl border border-[#e0e0e0] p-5 space-y-4">
            <h3 className="font-semibold">{lang === 'fr' ? 'Forfaits' : 'Packages'}</h3>
            {(config.packages || []).map((pkg, idx) => (
              <div key={pkg.id} className="border border-[#e0e0e0] rounded-lg p-4 space-y-3">
                <div className="grid grid-cols-3 gap-3">
                  <div><Label>Nom (FR)</Label><Input value={pkg.name_fr} onChange={e => { const p = [...config.packages]; p[idx] = { ...p[idx], name_fr: e.target.value }; setConfig(c => ({ ...c, packages: p })); }} className="mt-1" /></div>
                  <div><Label>Name (EN)</Label><Input value={pkg.name_en} onChange={e => { const p = [...config.packages]; p[idx] = { ...p[idx], name_en: e.target.value }; setConfig(c => ({ ...c, packages: p })); }} className="mt-1" /></div>
                  <div><Label>Prix ($)</Label><Input type="number" value={pkg.price} onChange={e => { const p = [...config.packages]; p[idx] = { ...p[idx], price: parseFloat(e.target.value) || 0 }; setConfig(c => ({ ...c, packages: p })); }} className="mt-1" /></div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div><Label>Description (FR)</Label><Textarea value={pkg.description_fr || ''} onChange={e => { const p = [...config.packages]; p[idx] = { ...p[idx], description_fr: e.target.value }; setConfig(c => ({ ...c, packages: p })); }} rows={2} className="mt-1" /></div>
                  <div><Label>Description (EN)</Label><Textarea value={pkg.description_en || ''} onChange={e => { const p = [...config.packages]; p[idx] = { ...p[idx], description_en: e.target.value }; setConfig(c => ({ ...c, packages: p })); }} rows={2} className="mt-1" /></div>
                </div>
              </div>
            ))}
          </div>

          {/* Extras */}
          <div className="bg-white rounded-xl border border-[#e0e0e0] p-5 space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="font-semibold">{lang === 'fr' ? 'Extras (coût unitaire)' : 'Extras (unit price)'}</h3>
              <button onClick={addExtra} className="flex items-center gap-1 px-3 py-1 text-xs rounded bg-[#e53a3a] text-white font-semibold"><Plus className="w-3 h-3" /> {lang === 'fr' ? 'Ajouter' : 'Add'}</button>
            </div>
            {(config.extras || []).map((ex, idx) => (
              <div key={ex.id} className="flex items-end gap-3">
                <div className="flex-1"><Label>Nom (FR)</Label><Input value={ex.name_fr} onChange={e => { const x = [...config.extras]; x[idx] = { ...x[idx], name_fr: e.target.value }; setConfig(c => ({ ...c, extras: x })); }} className="mt-1" /></div>
                <div className="flex-1"><Label>Name (EN)</Label><Input value={ex.name_en} onChange={e => { const x = [...config.extras]; x[idx] = { ...x[idx], name_en: e.target.value }; setConfig(c => ({ ...c, extras: x })); }} className="mt-1" /></div>
                <div className="w-24"><Label>Prix ($)</Label><Input type="number" value={ex.price} onChange={e => { const x = [...config.extras]; x[idx] = { ...x[idx], price: parseFloat(e.target.value) || 0 }; setConfig(c => ({ ...c, extras: x })); }} className="mt-1" /></div>
                <button onClick={() => removeExtra(idx)} className="p-2 text-red-600 hover:bg-red-50 rounded"><Trash2 className="w-4 h-4" /></button>
              </div>
            ))}
          </div>

          {/* Pickup Dates */}
          <div className="bg-white rounded-xl border border-[#e0e0e0] p-5 space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="font-semibold">{lang === 'fr' ? 'Dates de ramassage' : 'Pickup Dates'}</h3>
              <button onClick={addPickupDate} className="flex items-center gap-1 px-3 py-1 text-xs rounded bg-[#2E7D32] text-white font-semibold"><Plus className="w-3 h-3" /> {lang === 'fr' ? 'Ajouter' : 'Add'}</button>
            </div>
            <div className="flex flex-wrap gap-2">
              {(config.pickup_dates || []).map((d, idx) => (
                <Badge key={d} variant="outline" className="flex items-center gap-1 px-3 py-1.5">
                  {d}
                  <button onClick={() => removePickupDate(idx)} className="ml-1 text-red-500 hover:text-red-700">&times;</button>
                </Badge>
              ))}
            </div>
          </div>

          <button onClick={saveConfig} disabled={saving} className="btn-belgo px-6 py-2.5 rounded-lg text-white font-semibold bg-[#e53a3a] hover:bg-[#c62828]" data-testid="save-holiday-config">
            {saving ? '...' : (lang === 'fr' ? 'Enregistrer tout' : 'Save All')}
          </button>
        </div>
      )}

      {tab === 'orders' && (
        <div className="space-y-4">
          {orders.length === 0 ? (
            <p className="text-[#555555]">{lang === 'fr' ? 'Aucune commande' : 'No orders'}</p>
          ) : orders.map(order => (
            <div key={order.id} className={`bg-white rounded-xl border p-5 ${order.status === 'pending' ? 'border-[#f4d00d] border-2' : 'border-[#e0e0e0]'}`} data-testid={`holiday-order-${order.id}`}>
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <span className="font-bold text-[#e53a3a]">{order.order_number}</span>
                  {order.status === 'pending' && <Badge className="animate-pulse bg-[#f4d00d] text-black text-xs">NEW</Badge>}
                </div>
                <div className="flex items-center gap-2">
                  <Badge style={{ backgroundColor: order.status === 'pending' ? '#f4d00d' : order.status === 'confirmed' ? '#2E7D32' : order.status === 'ready' ? '#e53a3a' : '#555555', color: order.status === 'pending' ? '#000' : '#fff' }}>{order.status}</Badge>
                  <button onClick={() => printHolidayReceipt(order)} className="px-3 py-1 rounded text-xs font-medium bg-[#1a1a1a] text-white hover:bg-black">{lang === 'fr' ? 'Imprimer' : 'Print'}</button>
                </div>
              </div>
              <p className="text-sm text-[#555555]">{order.customer_name} | {order.customer_email} | {order.customer_phone}</p>
              <p className="text-sm font-medium">{order.package_name} x{order.quantity} | {lang === 'fr' ? 'Ramassage' : 'Pickup'}: {order.pickup_date}</p>
              {order.extras?.length > 0 && <p className="text-xs text-[#555555]">Extras: {order.extras.map(e => `${e.quantity}x ${e.name}`).join(', ')}</p>}
              <p className="text-sm font-bold text-[#e53a3a] mt-1">Total: {order.total?.toFixed(2)}$</p>
              {order.notes && <p className="text-xs italic text-[#555555]">"{order.notes}"</p>}
              <div className="flex gap-2 mt-3">
                {['confirmed', 'ready', 'completed', 'cancelled'].map(s => (
                  <button key={s} onClick={() => updateOrder(order.id, s)} disabled={order.status === s} className={`px-3 py-1 rounded text-xs font-medium border ${order.status === s ? 'opacity-50' : 'hover:opacity-80'}`} style={{ borderColor: s === 'ready' ? '#e53a3a' : s === 'confirmed' ? '#2E7D32' : '#555555', color: s === 'ready' ? '#e53a3a' : s === 'confirmed' ? '#2E7D32' : '#555555' }}>{s === 'ready' ? (lang === 'fr' ? 'Prête' : 'Ready') : s}</button>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// Main Admin Dashboard
// Code Export Admin (downloads full source as ZIP)
function CodeExportAdmin() {
  const { lang } = useLanguage();
  const [loading, setLoading] = useState(false);
  const [confirmed, setConfirmed] = useState(false);

  const handleExport = async () => {
    if (!confirmed) {
      toast.error(lang === 'fr' ? 'Veuillez d\'abord cocher la case de confirmation.' : 'Please check the confirmation box first.');
      return;
    }
    setLoading(true);
    try {
      const response = await axiosAuth.get(`${API}/admin/export/source`, {
        responseType: 'blob',
        timeout: 120000,
      });
      const blob = new Blob([response.data], { type: 'application/zip' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const ts = new Date().toISOString().slice(0, 19).replace(/[:T]/g, '-');
      a.download = `restobelgo-source-${ts}.zip`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      const count = response.headers['x-file-count'] || '?';
      toast.success(lang === 'fr' ? `Téléchargement démarré (${count} fichiers)` : `Download started (${count} files)`);
    } catch (err) {
      const detail = err.response?.data?.detail || err.message;
      toast.error((lang === 'fr' ? 'Erreur d\'export : ' : 'Export error: ') + detail);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-3xl space-y-6" data-testid="code-export-admin">
      <div>
        <h1 className="font-heading text-3xl font-bold text-[#1a1a1a] mb-1">
          {lang === 'fr' ? 'Exporter le code source' : 'Export source code'}
        </h1>
        <p className="text-sm text-[#555555]">
          {lang === 'fr'
            ? 'Téléchargez l\'intégralité du code (frontend + backend) en archive ZIP pour le redéployer ailleurs.'
            : 'Download the full source code (frontend + backend) as a ZIP archive to redeploy elsewhere.'}
        </p>
      </div>

      {/* Warning */}
      <div className="bg-[#fff7e6] border-2 border-[#f4d00d] rounded-xl p-5">
        <div className="flex items-start gap-3">
          <ShieldAlert className="w-6 h-6 text-[#e53a3a] flex-shrink-0 mt-0.5" />
          <div className="space-y-2 text-sm">
            <p className="font-bold text-[#1a1a1a]">
              {lang === 'fr' ? 'Avertissement de sécurité' : 'Security warning'}
            </p>
            <p className="text-[#555555]">
              {lang === 'fr'
                ? 'Le fichier ZIP contient l\'intégralité du code source de votre application. Gardez-le confidentiel et ne le partagez qu\'avec des développeurs de confiance.'
                : 'The ZIP file contains the full source code of your application. Keep it confidential and only share with trusted developers.'}
            </p>
          </div>
        </div>
      </div>

      {/* What's included */}
      <div className="bg-white rounded-xl border border-[#e0e0e0] p-5 space-y-3">
        <h3 className="font-semibold text-[#1a1a1a]">
          {lang === 'fr' ? 'Contenu de l\'archive' : 'Archive contents'}
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
          <div>
            <p className="font-semibold text-green-700 mb-1">✓ {lang === 'fr' ? 'Inclus' : 'Included'}</p>
            <ul className="text-[#555555] space-y-1 ml-4 list-disc">
              <li>{lang === 'fr' ? 'Code frontend React' : 'React frontend code'} (<code>/frontend/src</code>)</li>
              <li>{lang === 'fr' ? 'Code backend FastAPI' : 'FastAPI backend code'} (<code>/backend/*.py</code>)</li>
              <li><code>package.json</code>, <code>requirements.txt</code></li>
              <li>{lang === 'fr' ? 'Fichiers de config public' : 'Public config files'}</li>
              <li>EXPORT_README.md ({lang === 'fr' ? 'instructions' : 'instructions'})</li>
            </ul>
          </div>
          <div>
            <p className="font-semibold text-red-700 mb-1">✗ {lang === 'fr' ? 'Exclus (sécurité)' : 'Excluded (security)'}</p>
            <ul className="text-[#555555] space-y-1 ml-4 list-disc">
              <li><code>.env</code> ({lang === 'fr' ? 'clés MongoDB, JWT' : 'MongoDB keys, JWT'})</li>
              <li><code>node_modules/</code> ({lang === 'fr' ? 'à réinstaller' : 'reinstall via yarn'})</li>
              <li><code>.git/</code>, <code>__pycache__/</code></li>
              <li>{lang === 'fr' ? 'Fichiers de build/cache' : 'Build/cache files'}</li>
            </ul>
          </div>
        </div>
      </div>

      {/* Confirmation */}
      <label className="flex items-start gap-3 bg-white rounded-xl border border-[#e0e0e0] p-4 cursor-pointer hover:bg-[#f5f5f0] transition-colors">
        <input
          type="checkbox"
          checked={confirmed}
          onChange={(e) => setConfirmed(e.target.checked)}
          className="mt-1 w-5 h-5"
          data-testid="export-confirm-checkbox"
        />
        <span className="text-sm text-[#1a1a1a]">
          {lang === 'fr'
            ? 'Je comprends que ce ZIP contient le code source complet de Resto Belgo et je m\'engage à le garder confidentiel.'
            : 'I understand this ZIP contains the full source code of Resto Belgo and I will keep it confidential.'}
        </span>
      </label>

      {/* Download button */}
      <div className="flex items-center justify-between flex-wrap gap-4">
        <button
          onClick={handleExport}
          disabled={!confirmed || loading}
          className="inline-flex items-center gap-2 px-6 py-3 rounded-lg font-bold text-white bg-[#e53a3a] hover:bg-[#c62828] disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
          data-testid="export-download-btn"
        >
          <Download className="w-5 h-5" />
          {loading
            ? (lang === 'fr' ? 'Génération en cours...' : 'Generating...')
            : (lang === 'fr' ? 'Télécharger le code (ZIP)' : 'Download code (ZIP)')}
        </button>
        <p className="text-xs text-[#888]">
          {lang === 'fr' ? 'Génération typique : 5-20 secondes' : 'Typical generation: 5-20 seconds'}
        </p>
      </div>

      {/* Redeploy tips */}
      <details className="bg-white rounded-xl border border-[#e0e0e0] p-5">
        <summary className="cursor-pointer font-semibold text-[#1a1a1a]">
          {lang === 'fr' ? '🚀 Comment redéployer ailleurs ?' : '🚀 How to redeploy elsewhere?'}
        </summary>
        <div className="mt-4 space-y-3 text-sm text-[#555555]">
          <p><strong>1.</strong> {lang === 'fr' ? 'Créer une nouvelle base MongoDB' : 'Create a new MongoDB database'} (MongoDB Atlas — <a href="https://mongodb.com/cloud/atlas" target="_blank" rel="noopener noreferrer" className="text-[#e53a3a] underline">gratuit</a>)</p>
          <p><strong>2.</strong> {lang === 'fr' ? 'Recréer ' : 'Recreate '}<code>backend/.env</code>:</p>
          <pre className="bg-[#f5f5f0] p-3 rounded text-xs overflow-x-auto">
{`MONGO_URL=mongodb+srv://...
DB_NAME=restobelgo
JWT_SECRET=<random-64-char-string>`}
          </pre>
          <p><strong>3.</strong> {lang === 'fr' ? 'Recréer ' : 'Recreate '}<code>frontend/.env</code>:</p>
          <pre className="bg-[#f5f5f0] p-3 rounded text-xs overflow-x-auto">
{`REACT_APP_BACKEND_URL=https://your-backend.example.com`}
          </pre>
          <p><strong>4.</strong> {lang === 'fr' ? 'Backend' : 'Backend'}: <code>cd backend && pip install -r requirements.txt && uvicorn server:app</code></p>
          <p><strong>5.</strong> {lang === 'fr' ? 'Frontend' : 'Frontend'}: <code>cd frontend && yarn install && yarn build</code></p>
          <p><strong>6.</strong> {lang === 'fr' ? 'Reconfigurer SMTP/SMS via Admin → Notifications' : 'Reconfigure SMTP/SMS via Admin → Notifications'}</p>
        </div>
      </details>
    </div>
  );
}

export default function AdminDashboard() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (!loading && (!user || (user.role !== 'admin' && user.role !== 'superadmin'))) {
      navigate('/admin/login');
    }
  }, [loading, user, navigate]);

  if (loading) return <div className="min-h-screen flex items-center justify-center text-[#555555]">Loading...</div>;
  if (!user || (user.role !== 'admin' && user.role !== 'superadmin')) return null;

  return (
    <div className="flex min-h-screen" data-testid="admin-dashboard">
      <Sidebar active={location.pathname} />
      <div className="flex-1 p-6 lg:p-8 overflow-auto" style={{ background: '#f5f5f0' }}>
        <Routes>
          <Route index element={<StatsPage />} />
          <Route path="menu" element={<MenuManagement />} />
          <Route path="categories" element={<CategoriesManagement />} />
          <Route path="modifiers" element={<ModifiersManagement />} />
          <Route path="home-content" element={<HomeContentEditor />} />
          <Route path="gallery" element={<GalleryManagement />} />
          <Route path="ueat" element={<UEatSettings />} />
          <Route path="ordering" element={<OrderingTogglesAdmin />} />
          <Route path="jobs" element={<JobsAdmin />} />
          <Route path="retailers" element={<RetailersAdmin />} />
          <Route path="theme" element={<ThemeAdmin />} />
          <Route path="orders" element={<OrdersManagement />} />
          <Route path="catering" element={<CateringManagement />} />
          <Route path="foodtruck" element={<FoodTruckManagement />} />
          <Route path="messages" element={<MessagesManagement />} />
          <Route path="users" element={<UsersManagement />} />
          <Route path="holiday" element={<HolidayAdmin />} />
          <Route path="notifications" element={<NotificationsAdmin />} />
          <Route path="export" element={<CodeExportAdmin />} />
          <Route path="settings" element={<SiteSettings />} />
        </Routes>
      </div>
    </div>
  );
}
